import config from 'config/config';
import { useNavigate, useLocation } from 'react-router';
import { localInputToUtc, toLocalISOString } from 'common/utils/datetime';
import { Fragment, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { CommonEmbed } from 'components/common/CommonEmbed';
import { MapContainer, loadKakaoSDK } from 'components/map';
import { Meeting } from 'components/home/meetingTypes';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { CostInput } from 'pages/cost/CostInput';
import { MeetingRepository } from 'repository/meetingRepository';
import { FlashRepository } from 'repository/flashRepository';
import { NotificationRepository } from 'repository/notificationRepository';
import { PopupRepository } from 'repository/popupRepository';
import { AppPopupModal, AppPopupData } from 'components/common/AppPopupModal';
import { useUserInfo } from 'hooks/useAuth';
import { useUserLocation } from 'hooks/useUserLocation';
import { isNativeApp, setNativeMeetingSchedule, syncNativeMeetingSchedule, removeNativeMeetingSchedule, requestApp, WEB_TO_APP, openNativeRealtimeLocation } from 'common/webview';
import { openShare, getMeetingShareUrl } from 'common/utils/share';
import { isInKorea } from 'common/utils/regionDetector';
import { formatTimeUntil } from 'common/utils/todayCard';
import { JournalSummaryCard } from 'components/journal/JournalSummaryCard';
import type { JournalSummary } from 'hooks/useMeetingJournal';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapData } from 'common/utils/apiResponse';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import { usePullToRefresh } from 'hooks/usePullToRefresh';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { ThemeIcon } from './ThemeIcons';
import { StartGuide, QuickStart, DailyTip, HomeGames, StartPointer } from 'components/home/HomeGuide';
import {
    IcBell, IcChat, IcBug, IcPlus, IcClose, IcEnvelope, IcHome,
    IcCalendar, IcBallot, IcParty,
} from 'components/common/icons/EmojiIcons';
import { AppBar, AppGnb, Badge, BadgeTone, Bleed, Button, CardGrid, CardRail, FeedTagItem, HeroBanner, MeetingCard, PhotoStrip, PlaceCard, Section, SectionHeader, Sheet, TimeBadge, IconPin, IconPinOff, IconMap, IconShare } from 'components/ds';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { getFlashCTACopy } from 'common/utils/flashCta';
import { getGreeting } from 'common/utils/copyTone';
import hs from './Home.module.css';
import { fetchPlacePhotos, PlacePhoto } from 'common/utils/placePhotos';
import { getMeetingSchedule as getSchedule } from 'common/utils/format';
import { CreateMeetingSection } from 'components/meeting/CreateMeetingSection';

type ThemeItem = { emoji: string; title: string; subtitle: string; type: string; keyword?: string; imageUrl?: string; i18nKey?: string };

// 급번개 CTA 카피 — common/utils/flashCta 로 이전 (FlashCreate 등 다른 화면에서도 동일 카피 사용).

// 시즌 추천 (계절/연휴/시간대 — 동적)
const getSeasonalThemes = (): ThemeItem[] => {
    const month = new Date().getMonth() + 1;
    const day = new Date().getDate();
    const dayOfWeek = new Date().getDay();
    const hour = new Date().getHours();
    const themes: ThemeItem[] = [];

    // 시간대별 (title/subtitle 은 현재 언어로 — keyword 는 장소검색용 데이터라 유지)
    if (hour >= 17) themes.push({ emoji: '🍺', title: i18n.t('homeThemes.afterWorkT'), subtitle: i18n.t('homeThemes.afterWorkS'), type: 'bar', keyword: '호프 맥주', imageUrl: 'https://images.unsplash.com/photo-1436076863939-06870fe779c2?w=400&h=250&fit=crop' });
    if (hour >= 11 && hour <= 14) themes.push({ emoji: '🍽️', title: i18n.t('homeThemes.lunchT'), subtitle: i18n.t('homeThemes.lunchS'), type: 'restaurant', keyword: '맛집 점심', imageUrl: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=250&fit=crop' });
    if (dayOfWeek === 5) themes.push({ emoji: '🎉', title: i18n.t('homeThemes.fridayT'), subtitle: i18n.t('homeThemes.fridayS'), type: 'bar', keyword: '술집 이자카야', imageUrl: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=250&fit=crop' });
    if (dayOfWeek === 0 || dayOfWeek === 6) themes.push({ emoji: '☕', title: i18n.t('homeThemes.weekendT'), subtitle: i18n.t('homeThemes.weekendS'), type: 'cafe', keyword: '브런치 카페', imageUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=250&fit=crop' });

    // 계절별
    if (month >= 3 && month <= 5) {
        themes.push({ emoji: '🌸', title: i18n.t('homeThemes.cherryT'), subtitle: i18n.t('homeThemes.cherryS'), type: 'cafe', keyword: '벚꽃 명소', imageUrl: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🌿', title: i18n.t('homeThemes.springPicnicT'), subtitle: i18n.t('homeThemes.springPicnicS'), type: 'cafe', keyword: '공원 피크닉', imageUrl: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🚴', title: i18n.t('homeThemes.cyclingT'), subtitle: i18n.t('homeThemes.cyclingS'), type: 'sports', keyword: '자전거 한강', imageUrl: 'https://images.unsplash.com/photo-1541625602330-2277a4c46182?w=400&h=250&fit=crop' });
    } else if (month >= 6 && month <= 8) {
        themes.push({ emoji: '🏖️', title: i18n.t('homeThemes.summerVacaT'), subtitle: i18n.t('homeThemes.summerVacaS'), type: 'travel', keyword: '해수욕장', imageUrl: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🇻🇳', title: i18n.t('homeThemes.seAsiaT'), subtitle: i18n.t('homeThemes.seAsiaS'), type: 'travel', keyword: '동남아', imageUrl: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🏊', title: i18n.t('homeThemes.poolT'), subtitle: i18n.t('homeThemes.poolS'), type: 'sports', keyword: '워터파크 수영장', imageUrl: 'https://images.unsplash.com/photo-1576610616656-d3aa5d1f4534?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🍧', title: i18n.t('homeThemes.bingsuT'), subtitle: i18n.t('homeThemes.bingsuS'), type: 'cafe', keyword: '빙수', imageUrl: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400&h=250&fit=crop' });
    } else if (month >= 9 && month <= 11) {
        themes.push({ emoji: '🍂', title: i18n.t('homeThemes.autumnT'), subtitle: i18n.t('homeThemes.autumnS'), type: 'cafe', keyword: '단풍 명소', imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🍷', title: i18n.t('homeThemes.wineT'), subtitle: i18n.t('homeThemes.wineS'), type: 'bar', keyword: '와인바', imageUrl: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🇯🇵', title: i18n.t('homeThemes.japanAutumnT'), subtitle: i18n.t('homeThemes.japanAutumnS'), type: 'travel', keyword: '단풍', imageUrl: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=400&h=250&fit=crop' });
    } else {
        themes.push({ emoji: '⛷️', title: i18n.t('homeThemes.skiT'), subtitle: i18n.t('homeThemes.skiS'), type: 'travel', keyword: '스키장', imageUrl: 'https://images.unsplash.com/photo-1551524559-8af4e6624178?w=400&h=250&fit=crop' });
        themes.push({ emoji: '♨️', title: i18n.t('homeThemes.hotspringT'), subtitle: i18n.t('homeThemes.hotspringS'), type: 'travel', keyword: '온천', imageUrl: 'https://images.unsplash.com/photo-1540555700478-4be289fbec6c?w=400&h=250&fit=crop' });
        themes.push({ emoji: '🇯🇵', title: i18n.t('homeThemes.japanHotspringT'), subtitle: i18n.t('homeThemes.japanHotspringS'), type: 'travel', keyword: '온천', imageUrl: 'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=400&h=250&fit=crop' });
    }

    // 연휴/기념일 시즌
    if ((month === 1 && day >= 20) || (month === 2 && day <= 10))
        themes.unshift({ emoji: '🧧', title: i18n.t('homeThemes.lunarT'), subtitle: i18n.t('homeThemes.lunarS'), type: 'travel' });
    if ((month === 9 && day >= 10) || (month === 10 && day <= 5))
        themes.unshift({ emoji: '🌕', title: i18n.t('homeThemes.chuseokT'), subtitle: i18n.t('homeThemes.chuseokS'), type: 'travel' });
    if (month === 5 && day >= 1 && day <= 10) {
        themes.unshift({ emoji: '🎠', title: i18n.t('homeThemes.amusementT'), subtitle: i18n.t('homeThemes.amusementS'), type: 'entertainment' });
        themes.unshift({ emoji: '🦁', title: i18n.t('homeThemes.zooT'), subtitle: i18n.t('homeThemes.zooS'), type: 'travel' });
    }
    if (month === 12 && day >= 20)
        themes.unshift({ emoji: '🎄', title: i18n.t('homeThemes.christmasT'), subtitle: i18n.t('homeThemes.christmasS'), type: 'bar' });
    if (month === 3 && day >= 1 && day <= 14)
        themes.unshift({ emoji: '🤍', title: i18n.t('homeThemes.whiteDayT'), subtitle: i18n.t('homeThemes.whiteDayS'), type: 'cafe' });

    return themes;
};

// 고정 추천 주제 (여행 추천만 시즌별 동적)
// title/subtitle 은 렌더 시점에 t(`home.themes.${i18nKey}.title`) 로 lookup. keyword 는 검색 의도 — DB 매칭용 한글 그대로.
const getFixedThemes = (): ThemeItem[] => {
    const month = new Date().getMonth() + 1;
    let travelTheme: ThemeItem;
    if (month >= 3 && month <= 5) {
        travelTheme = { emoji: '🌸', title: '', subtitle: '', type: 'travel', i18nKey: 'travelSpring', imageUrl: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=250&fit=crop' };
    } else if (month >= 6 && month <= 8) {
        travelTheme = { emoji: '🏖️', title: '', subtitle: '', type: 'travel', i18nKey: 'travelSummer', imageUrl: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=250&fit=crop' };
    } else if (month >= 9 && month <= 11) {
        travelTheme = { emoji: '🍂', title: '', subtitle: '', type: 'travel', i18nKey: 'travelFall', imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=250&fit=crop' };
    } else {
        travelTheme = { emoji: '⛷️', title: '', subtitle: '', type: 'travel', i18nKey: 'travelWinter', imageUrl: 'https://images.unsplash.com/photo-1551524559-8af4e6624178?w=400&h=250&fit=crop' };
    }

    return [
        { emoji: '🍽️', title: '', subtitle: '', type: 'restaurant', i18nKey: 'restaurant', keyword: '맛집', imageUrl: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=250&fit=crop' },
        { emoji: '🍺', title: '', subtitle: '', type: 'bar', i18nKey: 'bar', keyword: '호프 맥주', imageUrl: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=250&fit=crop' },
        { emoji: '☕', title: '', subtitle: '', type: 'cafe', i18nKey: 'cafe', keyword: '카페', imageUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=250&fit=crop' },
        { emoji: '🎳', title: '', subtitle: '', type: 'entertainment', i18nKey: 'entertainment', keyword: '볼링장 노래방 당구장', imageUrl: 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=400&h=250&fit=crop' },
        { emoji: '🏋️', title: '', subtitle: '', type: 'sports', i18nKey: 'sports', keyword: '헬스장 피트니스', imageUrl: 'https://images.unsplash.com/photo-1541625602330-2277a4c46182?w=400&h=250&fit=crop' },
        travelTheme,
        { emoji: '⛳', title: '', subtitle: '', type: 'sports', i18nKey: 'golf', keyword: '스크린골프 골프연습장', imageUrl: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=400&h=250&fit=crop' },
        { emoji: '🎬', title: '', subtitle: '', type: 'entertainment', i18nKey: 'movie', keyword: '영화관', imageUrl: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&h=250&fit=crop' },
        { emoji: '📚', title: '', subtitle: '', type: 'cafe', i18nKey: 'reading', keyword: '북카페', imageUrl: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=400&h=250&fit=crop' },
    ];
};
const FIXED_THEMES = getFixedThemes();

export const Home = () => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { t } = useTranslation();
    const { nickname, profileImgPath, statusMessage, userNo: currentUserNo, isLoaded: userInfoLoaded } = useUserInfo();
    const [showNotification, setShowNotification] = useState(false);
    // 홈에서 정산 카드 클릭 시 in-place 모달
    const [costModal, setCostModal] = useState<{ meetingId: string } | null>(null);
    const [expandedTodayIds, setExpandedTodayIds] = useState<Set<string | number>>(new Set());
    const [toastMsg, setToastMsg] = useState('');
    const [showExtMapId, setShowExtMapId] = useState<string | null>(null);
    const [expandedParticipatingIds, setExpandedParticipatingIds] = useState<Set<string | number>>(new Set());
    const [participatingSubPlaces, setParticipatingSubPlaces] = useState<Record<string, any[]>>({});
    // 오늘 모임의 sub_places — 미니지도가 "다음 이동 장소" 를 가리키도록 자동 fetch
    const [todaySubPlaces, setTodaySubPlaces] = useState<Record<string, any[]>>({});
    // 오늘 모임별 「우리 기록」 요약 — 사진 넉 장 + 개수 + 최근 한 건
    const [journalSummaries, setJournalSummaries] = useState<Record<string, JournalSummary>>({});
    // today card 내 사용자가 chip 으로 선택한 장소 — meetingId 별
    const [todaySelectedKeys, setTodaySelectedKeys] = useState<Record<string, string>>({});
    const [locationSharingMap, setLocationSharingMap] = useState<Record<string, boolean>>({});


    // 진행중 급번개
    const [activeFlashes, setActiveFlashes] = useState<any[]>([]);
    // 메인 팝업 (wiz-common app-popup) — 활성 목록 중 우선순위 1개만 노출
    const [mainPopup, setMainPopup] = useState<AppPopupData | null>(null);
    // 오늘의 "내가 쏜다" 내역 — 홈 상단 축하 배너용
    const [todayTreats, setTodayTreats] = useState<any[]>([]);
    // 레이스 결과 맵 — 종료된 레이스 모임별 결과 행 (TOP3 + 꼴찌 표시용)
    const [raceResultsMap, setRaceResultsMap] = useState<Record<string, Array<{
        userNo: number; nickname: string; rank: number; arrived: boolean; isLast: boolean; profileImgUrl?: string;
    }>>>({});

    // 라이브 순위 맵 — 진행 중 레이스 모임별 나의 순위/거리/도착여부 (60초 polling)
    const [liveRaceMap, setLiveRaceMap] = useState<Record<string, {
        myRank: number | null;
        total: number;
        myDistanceMeters: number | null;
        myArrived: boolean | null;
    }>>({});

    // 오늘 모임별 대기 중 초대자 수
    const [pendingCountMap, setPendingCountMap] = useState<Record<string, number>>({});
    // 오늘 모임별 참여자(멤버) 미리보기
    const [todayMembersMap, setTodayMembersMap] = useState<Record<string, Array<{ userNo: number; nickname: string; profileUrl?: string; attendanceStatus?: string }>>>({});

    // 최근 모임 활동 알림 (참여자 합류 등) — 상단 카드 섹션
    const [recentActivity, setRecentActivity] = useState<Array<{ pushId: number; title: string; content: string; linkUrl: string; createdDt: string; pushValue: string }>>([]);
    // 초대/활동 배너 닫기 기록 — localStorage 로 영구 저장 (세션 닫혀도 유지)
    // 기존 sessionStorage 값도 병합해서 호환
    const [dismissedActivityIds, setDismissedActivityIds] = useState<Set<number>>(() => {
        try {
            const fromLocal: number[] = JSON.parse(localStorage.getItem('home_dismissed_activity') || '[]');
            const fromSession: number[] = JSON.parse(sessionStorage.getItem('home_dismissed_activity') || '[]');
            return new Set([...fromLocal, ...fromSession]);
        } catch { return new Set(); }
    });
    // 내가 현재 PENDING 상태로 받은 초대의 meetingId 집합 — 교차 검증으로 과거 푸시만 있는 잘못된 배너 차단
    const [pendingInvitedMeetingIds, setPendingInvitedMeetingIds] = useState<Set<string>>(new Set());
    const [pendingInvitedLoaded, setPendingInvitedLoaded] = useState<boolean>(false);
    // 푸시 미수신 대응 — meeting_invite 에서 직접 조회한 PENDING 초대 상세 (모임명 + 초대자)
    const [pendingInviteDetails, setPendingInviteDetails] = useState<Array<{
        inviteId: number; meetingId: string; meetingName: string; inviterNickname: string | null; invitedAt?: string;
    }>>([]);

    // 🏠 안전 귀가 watch 요청 (INVITED) — 실시간 배너
    const [safeReturnInvites, setSafeReturnInvites] = useState<Array<{
        sessionId: number; travelerNickname: string; travelerProfileUrl: string | null; destAddress: string; startedAt: string;
    }>>([]);

    // 1분 tick — 24h 만료 배너들이 화면 유지 중에도 자동으로 사라지도록 nowMs 주기 갱신
    const [nowMs, setNowMs] = useState<number>(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);
    const withinDay = useCallback((iso: string | null | undefined): boolean => {
        if (!iso) return true; // timestamp 없는 구버전 데이터는 남겨둠
        const t = new Date(iso).getTime();
        if (isNaN(t)) return true;
        return nowMs - t < 24 * 60 * 60 * 1000;
    }, [nowMs]);

    const loadActiveFlashes = useCallback(async () => {
        try {
            const res = await FlashRepository.onGetActive();
            const list = res?.data?.data || res?.data || [];
            // ★ 시작일이 어제 이전인 번개는 "다음날 자동 완료" 로 간주 — 홈에서 제외.
            //   백엔드 SELECT 도 동일 필터 (CURRENT_DATE) 적용했으나 캐시/지연 대비 프론트도 가드.
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const now = Date.now();
            const filtered = Array.isArray(list) ? list.filter((f: any) => {
                // 투표 마감시각이 지난 번개는 제외 — 마감된 번개는 홈에 노출하지 않음.
                // (서버는 VOTING→EXPIRED 자동 전이를 하지 않아 마감 후에도 active 로 내려옴)
                if (f.voteDeadline) {
                    const dl = new Date(f.voteDeadline).getTime();
                    if (!isNaN(dl) && dl <= now) return false;
                }
                // 상태가 VOTING 이 아니면(확정/취소/만료) 제외
                if (f.status && f.status !== 'VOTING') return false;
                if (!f.startDatetime) return true;
                const t = new Date(f.startDatetime).getTime();
                if (isNaN(t)) return true;
                return t >= todayStart.getTime();
            }) : [];
            setActiveFlashes(filtered);
        } catch { /* 무시 */ }
    }, []);

    // 메인 팝업 1회 fetch — 사용자별 NEVER/TODAY 필터링은 서버에서 처리
    useEffect(() => {
        const uid = sessionStorage.getItem('userId') || '';
        if (!uid) return;
        PopupRepository.onGetActive(uid, 'ko')
            .then((res: any) => {
                const list = res?.data?.data || res?.data || [];
                if (Array.isArray(list) && list.length > 0) {
                    const p = list[0]; // 백엔드 priority 정렬 — 가장 우선 팝업 1개만
                    setMainPopup({
                        popupId: p.popupId,
                        title: p.title,
                        content: p.content,
                        imageUrl: p.imageUrl,
                        confirmText: p.confirmText,
                        showAgain: p.showAgain,
                    });
                }
            })
            .catch(() => { /* 팝업 실패는 홈 흐름과 무관 — 무시 */ });
    }, []);

    const loadTodayTreats = useCallback(async () => {
        try {
            const res = await instance.get(`${getApiUrl('meetings').replace('/meetings', '')}/payments/today-treats`);
            const list = res?.data?.data || res?.data || [];
            setTodayTreats(Array.isArray(list) ? list : []);
        } catch { /* 무시 */ }
    }, []);

    useEffect(() => {
        loadActiveFlashes();
        loadTodayTreats();
        const interval = setInterval(loadActiveFlashes, 15000);
        return () => clearInterval(interval);
    }, [loadActiveFlashes, loadTodayTreats]);


    // 지난 모임 "다시 만들기" 상태
    const [recreateSourceId, setRecreateSourceId] = useState<string | null>(null);
    const [recreateSource, setRecreateSource] = useState<any>(null);
    const [recreateDate, setRecreateDate] = useState('');
    const [recreateStartTime, setRecreateStartTime] = useState(''); // HH:MM
    const [recreateEndTime, setRecreateEndTime] = useState('');     // HH:MM
    const [recreatePlace, setRecreatePlace] = useState<{ name: string; address: string; lat?: number; lng?: number; externalPlaceId?: string } | null>(null);
    const [isRecreating, setIsRecreating] = useState(false);


    // 최근 모임 활동 알림 로드 (참여자 합류 MEETING_MEMBER_JOINED 등)
    useEffect(() => {
        const userNo = Number(sessionStorage.getItem('userNo')) || 0;
        if (!userNo) return;
        const HOURS_24 = 24 * 60 * 60 * 1000;
        NotificationRepository.onGetPushHistory({ userNo, pageSize: 30 })
            .then((res) => {
                const list = res?.data?.data?.list || [];
                const cutoff = Date.now() - HOURS_24;
                const filtered = list
                    .filter((p: any) => {
                        if (!p?.createdDt) return false;
                        if (new Date(p.createdDt).getTime() < cutoff) return false;
                        // 상단 하이라이트 대상: 초대 받음 / 참여 합류 / 일정 확정 / 새 투표
                        return ['MEETING_INVITED', 'MEETING_MEMBER_JOINED', 'SCHEDULE_CONFIRMED', 'SCHEDULE_PROPOSED'].includes(p.pushValue);
                    })
                    .slice(0, 5);
                setRecentActivity(filtered);
            })
            .catch(() => { /* 조용히 무시 */ });
    }, []);

    // MEETING_MEMBER_JOINED 배너 — 한번 노출되면 localStorage 에 seen 기록.
    // 현재 마운트에서는 계속 보여주되, 다음 방문 시 이미 seen 이면 필터 단계에서 제외됨.
    const initialDismissedRef = useRef<Set<number>>(new Set());
    useEffect(() => {
        initialDismissedRef.current = new Set(dismissedActivityIds);
        // 최초 마운트 시 현재 dismissed 상태를 스냅샷 (이후 자동 seen 추가는 스냅샷에 반영 안 됨)
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!recentActivity.length) return;
        const joinedIds = recentActivity
            .filter(a => a.pushValue === 'MEETING_MEMBER_JOINED')
            .map(a => a.pushId);
        if (!joinedIds.length) return;
        try {
            const current: number[] = JSON.parse(localStorage.getItem('home_dismissed_activity') || '[]');
            const merged = Array.from(new Set([...current, ...joinedIds]));
            localStorage.setItem('home_dismissed_activity', JSON.stringify(merged));
        } catch { /* ignore */ }
    }, [recentActivity]);

    // 🏠 안전 귀가 watch 요청 로드 (INVITED)
    useEffect(() => {
        let cancelled = false;
        import('repository/safeReturnRepository').then(({ SafeReturnRepository }) => {
            SafeReturnRepository.onGetInvitedWatchRequests()
                .then((res: any) => {
                    if (cancelled) return;
                    const list: any[] = res?.data?.data || res?.data || [];
                    if (!Array.isArray(list)) return;
                    const mapped = list.map(s => ({
                        sessionId: Number(s.sessionId),
                        travelerNickname: (() => {
                            const n = s.travelerNickname || '';
                            try { return decodeURIComponent(n); } catch { return n; }
                        })(),
                        travelerProfileUrl: s.travelerProfileUrl || null,
                        destAddress: s.destAddress || '',
                        startedAt: s.startedAt || '',
                    }));
                    setSafeReturnInvites(mapped);
                })
                .catch(() => { /* 무시 */ });
        });
        return () => { cancelled = true; };
    }, []);

    // 실제로 현재 PENDING 상태인 내 초대 — 배너 직접 렌더 + 교차 검증용
    // 같은 모임 PENDING 중복 행이 있어도 배너는 1건만 (최신 inviteId 우선)
    useEffect(() => {
        MeetingRepository.onGetMyPendingInviteDetails()
            .then((res: any) => {
                const rawList: any[] = res?.data?.data || res?.data || [];
                const list = Array.isArray(rawList) ? rawList : [];
                const seen = new Set<string>();
                const mapped: typeof pendingInviteDetails = [];
                for (const r of list) {
                    const mid = String(r.meetingId);
                    if (seen.has(mid)) continue;
                    seen.add(mid);
                    mapped.push({
                        inviteId: Number(r.inviteId),
                        meetingId: mid,
                        meetingName: r.meetingName ? decodeURIComponent(r.meetingName).replace(/\+/g, ' ') : '',
                        inviterNickname: r.inviterNickname ? decodeURIComponent(r.inviterNickname).replace(/\+/g, ' ') : null,
                        invitedAt: r.invitedAt,
                    });
                }
                setPendingInviteDetails(mapped);
                setPendingInvitedMeetingIds(new Set(mapped.map(m => m.meetingId)));
            })
            .catch(() => { /* 무시 */ })
            .finally(() => setPendingInvitedLoaded(true));
    }, []);

    // (레이스 결과 일괄 로드 useEffect 는 data 선언 이후로 이동 — 아래 참조)

    // 지난 모임 "다시 만들기" — 상세 로드 후 날짜 선택 모달 오픈
    const openRecreate = useCallback(async (pastMeetingId: string) => {
        if (!pastMeetingId || recreateSourceId === pastMeetingId) return;
        setRecreateSourceId(pastMeetingId);
        try {
            const res = await MeetingRepository.onGetMeetingDetail(pastMeetingId);
            const detail = res?.data?.data || res?.data;
            if (!detail) throw new Error('detail load failed');
            setRecreateSource(detail);

            // 기본 날짜: 내일
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const y = tomorrow.getFullYear();
            const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
            const d = String(tomorrow.getDate()).padStart(2, '0');
            setRecreateDate(`${y}-${m}-${d}`);

            // 기본 시간: 원본 모임 시간
            const srcSched = detail.schedule || detail.confirmedSchedule || {};
            const hhmm = (iso?: string) => {
                if (!iso) return '';
                const dt = new Date(iso);
                return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
            };
            setRecreateStartTime(hhmm(srcSched.startDatetime) || '12:00');
            setRecreateEndTime(hhmm(srcSched.endDatetime) || '14:00');

            // 기본 장소: 원본 모임 장소
            const place = detail.place;
            if (place?.placeName || place?.placeAddress) {
                setRecreatePlace({
                    name: place.placeName || '',
                    address: place.placeAddress || '',
                    lat: place.latitude ? parseFloat(place.latitude) : undefined,
                    lng: place.longitude ? parseFloat(place.longitude) : undefined,
                    externalPlaceId: place.externalPlaceId,
                });
            } else {
                setRecreatePlace(null);
            }
        } catch {
            setRecreateSourceId(null);
            setToastMsg(t('home.toastDetailLoadFailed'));
            setTimeout(() => setToastMsg(''), 2500);
        }
    }, [recreateSourceId]);

    // 장소 변경 후 복귀 — recreate_pending 복원 + 새 장소 적용
    useEffect(() => {
        const pendingRaw = sessionStorage.getItem('recreate_pending');
        if (!pendingRaw) return;
        const picked = (routerLocation as any).state?.selectedPlaceInfo;
        if (!picked) return; // 장소 선택 없이 돌아온 경우 무시

        try {
            const pending = JSON.parse(pendingRaw);
            sessionStorage.removeItem('recreate_pending');

            // 모달 재오픈 (상세 재조회) → 그 다음 날짜/시간/장소 복원
            (async () => {
                await openRecreate(pending.sourceId);
                setRecreateDate(pending.date);
                setRecreateStartTime(pending.startTime);
                setRecreateEndTime(pending.endTime);
                setRecreatePlace({
                    name: picked.name || '',
                    address: picked.address || '',
                    lat: picked.lat,
                    lng: picked.lng,
                    externalPlaceId: picked.externalPlaceId,
                });
            })();

            // navigate state 정리 (동일 state로 재실행 방지)
            window.history.replaceState(null, '');
        } catch {
            sessionStorage.removeItem('recreate_pending');
        }
    }, [routerLocation, openRecreate]);

    const closeRecreate = () => {
        setRecreateSourceId(null);
        setRecreateSource(null);
        setRecreateDate('');
        setRecreateStartTime('');
        setRecreateEndTime('');
        setRecreatePlace(null);
        // 장소 선택 대기 상태도 클리어
        sessionStorage.removeItem('recreate_pending');
    };

    // 장소 변경 — 추천 장소 화면으로 이동, 선택 결과는 location.state로 돌아옴
    const goChangePlace = () => {
        if (!recreateSourceId || !recreateSource) return;
        // 모달 상태 보존 (페이지 이동 중에도 날짜/시간 기억)
        sessionStorage.setItem('recreate_pending', JSON.stringify({
            sourceId: recreateSourceId,
            date: recreateDate,
            startTime: recreateStartTime,
            endTime: recreateEndTime,
        }));
        navigate('/meeting/create/place-explore', {
            state: { mode: 'recreate', returnTo: '/home' }
        });
    };

    const confirmRecreate = async () => {
        if (!recreateSource || !recreateDate || !recreateStartTime || !recreateEndTime || isRecreating) return;
        setIsRecreating(true);
        try {
            const src = recreateSource;

            // 새 날짜 + 사용자 입력 시간
            // 서버는 시각을 UTC 로 받습니다 (예전에는 여기서 벽시계를 조립했습니다)
            const buildLocalISO = (dateStr: string, hhmm: string) =>
                localInputToUtc(`${dateStr}T${hhmm}`);
            const [startH, startM] = recreateStartTime.split(':').map(Number);
            const [endH, endM] = recreateEndTime.split(':').map(Number);

            // 종료 시간이 시작보다 빠르면 다음날로 간주
            const endDateStr = (() => {
                const startMin = startH * 60 + startM;
                const endMin = endH * 60 + endM;
                if (endMin <= startMin) {
                    const [y, m, d] = recreateDate.split('-').map(Number);
                    const nd = new Date(y, m - 1, d + 1);
                    const pad = (n: number) => String(n).padStart(2, '0');
                    return `${nd.getFullYear()}-${pad(nd.getMonth() + 1)}-${pad(nd.getDate())}`;
                }
                return recreateDate;
            })();

            const payload: any = {
                meetingName: src.meetingName || t('home.toastNewMeetingDefault'),
                description: src.description || '',
                category: src.category || 'ETC',
                maxMembers: src.maxMembers || 10,
                isPublic: src.isPublic !== false,
                isRegularMeeting: false,
                meetingType: src.meetingType || 'DAY',
                startDatetime: buildLocalISO(recreateDate, recreateStartTime),
                endDatetime: buildLocalISO(endDateStr, recreateEndTime),
            };
            // 사용자가 선택한 새 장소 우선, 없으면 원본 장소 유지
            if (recreatePlace && (recreatePlace.name || recreatePlace.address)) {
                payload.placeName = recreatePlace.name;
                payload.placeAddress = recreatePlace.address;
                payload.latitude = recreatePlace.lat;
                payload.longitude = recreatePlace.lng;
                if (recreatePlace.externalPlaceId) payload.externalPlaceId = recreatePlace.externalPlaceId;
            }

            const res = await MeetingRepository.onPostCreateMeeting(payload);
            const resData = res?.data?.data || res?.data;
            const newId = typeof resData === 'string' ? resData : resData?.meetingId;
            closeRecreate();
            if (newId) {
                setToastMsg(t('home.toastNewMeetingCreated'));
                setTimeout(() => setToastMsg(''), 2500);
                meetingStore.onGetMeetingList();
                navigate(`/meeting/detail/${newId}`);
            }
        } catch {
            setToastMsg(t('home.toastCreateFailed'));
            setTimeout(() => setToastMsg(''), 2500);
        } finally {
            setIsRecreating(false);
        }
    };

    // localStorage에서 위치공유 상태 초기화
    useEffect(() => {
        const map: Record<string, boolean> = {};
        try {
            const keys = Object.keys(localStorage);
            keys.forEach(k => {
                if (k.startsWith('locationSharing_')) {
                    const id = k.replace('locationSharing_', '');
                    map[id] = localStorage.getItem(k) === 'true';
                }
            });
        } catch {}
        setLocationSharingMap(map);
    }, []);

    const toggleLocationSharing = async (meetingId: string | number) => {
        const key = String(meetingId);
        const newVal = !locationSharingMap[key];
        // 낙관적 UI
        setLocationSharingMap(prev => ({ ...prev, [key]: newVal }));
        localStorage.setItem(`locationSharing_${key}`, String(newVal));

        // 백엔드 DB 의 meeting_member.location_sharing 업데이트 — 이게 빠지면
        // dokocrew-api 의 LocationSharingService 가 Redis Keyspace Notification 받아도
        // selectLocationSharingMeetingIds 가 0건 → STOMP broadcast 자체가 발송 안 됨
        try {
            await MeetingRepository.onPutMyMembership(key, { locationSharing: newVal });
        } catch (e) {
            // 실패 → 롤백
            setLocationSharingMap(prev => ({ ...prev, [key]: !newVal }));
            localStorage.setItem(`locationSharing_${key}`, String(!newVal));
            setToastMsg(t('meeting.locationSharingFailed'));
            setTimeout(() => setToastMsg(''), 2500);
            return;
        }

        // 네이티브에 스케줄 전달/제거
        if (isNativeApp()) {
            const meeting = todayMeetings.find(m => String(m.id) === key);
            if (newVal) {
                syncNativeMeetingSchedule(key, meeting);
            } else if (!newVal) {
                removeNativeMeetingSchedule(key);   // 이 모임만 해제(전체 clear 금지)
            }
        }

        // 토스트 표시
        const msg = newVal ? t('meeting.locationSharingEnabled') : t('meeting.locationSharingDisabled');
        setToastMsg(msg);
        setTimeout(() => setToastMsg(''), 2500);
    };
    const { fetchLocation, location: userGpsLocation } = useUserLocation();
    const { data, status } = useMeetingList();
    // 최초 로딩에만 스켈레톤 — 백그라운드 리프레시(refreshAll/포커스/PTR) 중에는
    // 이미 받아둔 data 를 그대로 보여줘야 화면이 깜빡이지 않고 스크롤 위치도 유지됨.
    const isLoading = (status === 'pending' || status === 'default') && !data;

    useEffect(() => {
        meetingStore.onGetMeetingList();
        fetchLocation(); // 홈 진입 시 위치 미리 요청 (5분 캐시)
    }, []);

    // 전체 데이터 리프레시 — 홈 복귀/탭 포커스/푸시 탭/PTR 에서 호출
    // Promise 를 반환하므로 호출자가 await 가능 (PTR 스피너 동기화용)
    const refreshAll = useCallback(async () => {
        const pendingInvitePromise = MeetingRepository.onGetMyPendingInviteDetails()
            .then((res: any) => {
                const rawList: any[] = res?.data?.data || res?.data || [];
                const list = Array.isArray(rawList) ? rawList : [];
                const seen = new Set<string>();
                const mapped: typeof pendingInviteDetails = [];
                for (const r of list) {
                    const mid = String(r.meetingId);
                    if (seen.has(mid)) continue;
                    seen.add(mid);
                    mapped.push({
                        inviteId: Number(r.inviteId),
                        meetingId: mid,
                        meetingName: r.meetingName ? decodeURIComponent(r.meetingName).replace(/\+/g, ' ') : '',
                        inviterNickname: r.inviterNickname ? decodeURIComponent(r.inviterNickname).replace(/\+/g, ' ') : null,
                        invitedAt: r.invitedAt,
                    });
                }
                setPendingInviteDetails(mapped);
                setPendingInvitedMeetingIds(new Set(mapped.map(m => m.meetingId)));
                setPendingInvitedLoaded(true);
            })
            .catch(() => { /* 무시 */ });

        // 최근 활동 푸시 이력 재조회
        const userNo = Number(sessionStorage.getItem('userNo')) || 0;
        const activityPromise = userNo
            ? NotificationRepository.onGetPushHistory({ userNo, pageSize: 30 })
                .then((res) => {
                    const HOURS_24 = 24 * 60 * 60 * 1000;
                    const list = res?.data?.data?.list || [];
                    const cutoff = Date.now() - HOURS_24;
                    const filtered = list
                        .filter((p: any) => {
                            if (!p?.createdDt) return false;
                            if (new Date(p.createdDt).getTime() < cutoff) return false;
                            return ['MEETING_INVITED', 'MEETING_MEMBER_JOINED', 'SCHEDULE_CONFIRMED', 'SCHEDULE_PROPOSED'].includes(p.pushValue);
                        })
                        .slice(0, 5);
                    setRecentActivity(filtered);
                })
                .catch(() => { /* 무시 */ })
            : Promise.resolve();

        await Promise.all([
            meetingStore.onGetMeetingList(),
            loadActiveFlashes(),
            loadTodayTreats(),
            pendingInvitePromise,
            activityPromise,
        ]);
    }, [loadActiveFlashes, loadTodayTreats]);

    // 홈 경로로 이동할 때마다 리프레시 (같은 Home 컴포넌트가 마운트된 채로 location.key 만 바뀌는 경우 포함)
    useEffect(() => {
        if (routerLocation.pathname === '/home' || routerLocation.pathname === '/') {
            refreshAll();
        }
    }, [routerLocation.key, routerLocation.pathname, refreshAll]);

    // 브라우저/WebView 가시성 변경 → 포그라운드 복귀 시 리프레시
    useEffect(() => {
        const handleVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshAll();
            }
        };
        const handlePageShow = (e: PageTransitionEvent) => {
            // back-forward 캐시에서 복원된 경우도 포함
            if (e.persisted) refreshAll();
        };
        document.addEventListener('visibilitychange', handleVisible);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('focus', refreshAll);
        return () => {
            document.removeEventListener('visibilitychange', handleVisible);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('focus', refreshAll);
        };
    }, [refreshAll]);

    // 오늘 모임별 대기 초대자 수 로드 — 카드 칩 표시용
    useEffect(() => {
        const raw = data?.data;
        if (!Array.isArray(raw)) return;
        const now = Date.now();
        const todayOnly = raw.filter((m: any) => {
            const s = m?.schedule?.startDatetime;
            if (!s) return false;
            const start = new Date(s);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const next = new Date(today);
            next.setDate(next.getDate() + 1);
            return start >= today && start < next;
        });
        todayOnly.forEach(async (m: any) => {
            const mid = m.meetingId;
            if (!mid) return;
            // 대기 초대자 수
            if (pendingCountMap[mid] == null) {
                try {
                    const res: any = await MeetingRepository.onGetPendingInvites(mid);
                    const list = res?.data?.data || [];
                    setPendingCountMap(prev => ({ ...prev, [mid]: Array.isArray(list) ? list.length : 0 }));
                } catch { /* ignore */ }
            }
            // 참여 멤버 — 카드 아바타 미리보기용
            if (!todayMembersMap[mid]) {
                try {
                    const res: any = await MeetingRepository.onGetMeetingMembers(mid);
                    const list = res?.data?.data || res?.data || [];
                    const mapped = list.map((mm: any) => ({
                        userNo: Number(mm.userNo),
                        nickname: (() => { try { return decodeURIComponent(mm.nickname || ''); } catch { return mm.nickname || ''; } })(),
                        profileUrl: mm.profileUrl || mm.profileImgUrl || mm.profileImgPath || undefined,
                        attendanceStatus: mm.attendanceStatus,
                    })).filter((mm: any) => mm.userNo > 0);
                    setTodayMembersMap(prev => ({ ...prev, [mid]: mapped }));
                } catch { /* ignore */ }
            }
        });
        void now;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // 레이스 종료된 모임들의 결과 일괄 로드 — TOP3+꼴찌 요약/축하 배너용
    // data 가 바뀔 때만 재실행 (meetings 는 매 렌더 새 배열이라 deps 로 부적합)
    useEffect(() => {
        const raw = data?.data;
        if (!Array.isArray(raw)) return;
        const finalizedMeetings = raw.filter((m: any) => m.raceEnabled && m.raceFinalizedAt);
        if (finalizedMeetings.length === 0) return;

        finalizedMeetings.forEach(async (m: any) => {
            const mid = m.meetingId;
            if (raceResultsMap[mid]) return; // 이미 로드됐으면 스킵
            try {
                const res: any = await MeetingRepository.onGetRaceResult(mid);
                const list = res?.data?.data;
                if (Array.isArray(list) && list.length > 0) {
                    setRaceResultsMap(prev => ({
                        ...prev,
                        [mid]: list.map((r: any) => ({
                            userNo: Number(r.userNo),
                            nickname: r.nickname || t('home.fallbackUserName', { userNo: r.userNo }),
                            rank: Number(r.rankOrder || 0),
                            arrived: Boolean(r.arrived),
                            isLast: Boolean(r.isLast),
                            profileImgUrl: r.profileImgUrl || undefined,
                        })),
                    }));
                }
            } catch { /* 결과 없으면 조용히 무시 */ }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // 🏁 라이브 레이스 순위 polling — race-enabled + 시작 1시간 전 ~ 종료 + 미확정 모임만 60초 간격으로 호출
    useEffect(() => {
        const raw = data?.data;
        if (!Array.isArray(raw)) return;

        const fetchLive = async () => {
            const now = Date.now();
            const targets = raw.filter((m: any) => {
                if (!m.raceEnabled || m.raceFinalizedAt) return false;
                const sched = m.schedule || m.confirmedSchedule;
                const startMs = sched?.startDatetime ? new Date(sched.startDatetime).getTime() : null;
                const endMs = sched?.endDatetime ? new Date(sched.endDatetime).getTime() : null;
                if (startMs == null) return false;
                // 시작 1시간 전 ~ 종료 (또는 종료 없으면 시작+15분)
                const windowEnd = endMs ?? startMs + 15 * 60_000;
                return now >= startMs - 60 * 60_000 && now <= windowEnd;
            });

            if (targets.length === 0) return;

            await Promise.all(targets.map(async (m: any) => {
                try {
                    const res: any = await MeetingRepository.onGetRaceLive(m.meetingId);
                    const d = res?.data?.data;
                    if (!d || !d.active) return;
                    setLiveRaceMap(prev => ({
                        ...prev,
                        [m.meetingId]: {
                            myRank: d.myRank ?? null,
                            total: d.total ?? 0,
                            myDistanceMeters: d.myDistanceMeters ?? null,
                            myArrived: d.myArrived ?? null,
                        }
                    }));
                } catch { /* 폴링 실패는 조용히 */ }
            }));
        };

        fetchLive();
        const interval = setInterval(fetchLive, 60_000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const userName = nickname || sessionStorage.getItem('userName') || (userInfoLoaded ? t('home.userFallback') : '');


    // 모임 생성 이력 — OnboardingStack 노출 차단용 (한 번이라도 만들었으면 가이드 불필요)
    const hasCreatedMeeting = useMemo(() => {
        const raw = data?.data;
        if (!Array.isArray(raw) || !currentUserNo) return false;
        return raw.some((m: any) => Number(m.creatorNo) === Number(currentUserNo));
    }, [data, currentUserNo]);

    const meetings: Meeting[] = data?.data?.map((meeting: any) => {
        const schedule = getSchedule(meeting);

        const calculateStatus = () => {
            // 종료 시각이 없으면 끝났는지 알 수 없어 '예정' 으로 둡니다.
            // (예전에는 new Date(undefined) 가 Invalid Date 라 비교가 늘 false →
            //  같은 결과였는데, 의도가 드러나지 않았습니다)
            if (!schedule?.endDatetime) return "예정";
            const now = new Date();
            const startDate = new Date(schedule.startDatetime);
            const endDate = new Date(schedule.endDatetime);
            if (now > endDate) return "종료";
            if (now >= startDate && now <= endDate) return "진행중";
            return "예정";
        };

        const calculateDDay = () => {
            if (!schedule) return undefined;
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);
            const meetingDate = new Date(schedule.startDatetime);
            meetingDate.setHours(0, 0, 0, 0);
            const diffDays = Math.round((meetingDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? `D-${diffDays}` : diffDays === 0 ? "D-Day" : `D+${Math.abs(diffDays)}`;
        };

        const place = meeting.place;
        const isLocationUndecided = !place?.placeName && !place?.placeAddress;

        return {
            id: meeting.meetingId,
            inviteCode: meeting.inviteCode,
            title: meeting.meetingName,
            creatorNo: meeting.creatorNo != null ? Number(meeting.creatorNo) : undefined,
            host: meeting.creatorNickname ? decodeURIComponent(meeting.creatorNickname) : t('meeting.unknownUser'),
            status: calculateStatus(),
            // 종료 시각이 없으면 시작만 씁니다 (예전에는 'Invalid Date' 가 그대로 찍혔습니다)
            time: schedule
                ? [schedule.startDatetime, schedule.endDatetime]
                    .filter(Boolean)
                    .map(d => new Date(d as string).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }))
                    .join(' - ')
                : "",
            date: schedule
                ? new Date(schedule.startDatetime).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })
                : "",
            location: place?.placeAddress || place?.placeName || "",
            placeName: place?.placeName || "",
            placeAddress: place?.placeAddress || "",
            latitude: place?.latitude ? parseFloat(place.latitude) : 0,
            longitude: place?.longitude ? parseFloat(place.longitude) : 0,
            // 카드 사진용 — 있으면 placePhotos 로 장소 사진을 받아 씁니다
            externalPlaceId: place?.externalPlaceId || null,
            /* 기록(사진·글)을 남길 때 쓰는 장소 키.
               buildAllPlaces 가 집결지의 placeId 를 여기서 읽는데 담고 있지
               않아서, 오늘 카드의 「우리 기록」이 한 번도 뜨지 않았습니다
               (activePlace.placeId 가 늘 null 이었습니다). */
            placeKey: place?.placeId || place?.externalPlaceId || null,
            hostProfileUrl: meeting.creatorProfileUrl || null,
            dDay: calculateDDay(),
            isRegularMeeting: meeting.isRegularMeeting || false,
            scheduleStartDateTime: schedule?.startDatetime,
            scheduleEndDateTime: schedule?.endDatetime,
            // 목록 API 가 주는 이름은 currentMemberCount 입니다.
            // meeting.memberCount 는 응답에 없어 항상 1 로 떨어지던 것을 바로잡습니다.
            memberCount: meeting.currentMemberCount ?? meeting.memberCount ?? 1,
            attendingCount: meeting.attendingCount ?? 0,
            maxMembers: meeting.maxMembers || 10,
            hasUnsettled: meeting.hasUnsettledPayment || false,
            isLocationUndecided,
            isScheduleUndecided: !schedule,
            meetingType: meeting.meetingType || 'DAY',
            totalPaymentAmount: meeting.totalPaymentAmount || 0,
            myUnsettledAmount: meeting.myUnsettledAmount || 0,
            hasActiveProposal: meeting.hasActiveProposal || false,
            proposalVoteCount: meeting.proposalVoteCount || 0,
            myAttendanceStatus: meeting.myAttendanceStatus || 'UNDECIDED',
            hotelName: meeting.hotelName || '',
            hotelAddress: meeting.hotelAddress || '',
            myPaidAmount: meeting.myPaidAmount || 0,
            myBalanceAmount: meeting.myBalanceAmount || 0,
            raceEnabled: Boolean(meeting.raceEnabled),
            raceFinalizedAt: meeting.raceFinalizedAt || undefined,
            endedAt: meeting.endedAt || null,
            paymentSettledAt: meeting.paymentSettledAt || null,
        };
    }) || [];

    // 날짜 기반 필터링
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);

    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const upcomingSoonMeeting = meetings.find(m => {
        if (!m.scheduleStartDateTime) return false;
        const meetingTime = new Date(m.scheduleStartDateTime);
        const endTime = m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime) : null;
        if (endTime && endTime < now) return false;
        return meetingTime <= oneHourLater;
    });

    const sortBySchedule = (a: Meeting, b: Meeting) => {
        if (!a.scheduleStartDateTime && !b.scheduleStartDateTime) return 0;
        if (!a.scheduleStartDateTime) return 1;
        if (!b.scheduleStartDateTime) return -1;
        return new Date(a.scheduleStartDateTime).getTime() - new Date(b.scheduleStartDateTime).getTime();
    };

    // 오늘 모임 전용 — "현재 진행중 → 임박 예정 → 이미 종료" 순으로 정렬.
    //  · 진행중(start ≤ now ≤ end): group 0, 종료 시각 빠른 순
    //  · 예정(start > now): group 1, 시작 시각 빠른 순 (제일 가까운 모임이 위)
    //  · 종료(end < now): group 2, 종료 시각 최근 순
    const sortTodayByProximity = (a: Meeting, b: Meeting) => {
        const nowMs = Date.now();
        const aStart = a.scheduleStartDateTime ? new Date(a.scheduleStartDateTime).getTime() : null;
        const aEnd = a.scheduleEndDateTime ? new Date(a.scheduleEndDateTime).getTime() : aStart;
        const bStart = b.scheduleStartDateTime ? new Date(b.scheduleStartDateTime).getTime() : null;
        const bEnd = b.scheduleEndDateTime ? new Date(b.scheduleEndDateTime).getTime() : bStart;
        const groupOf = (start: number | null, end: number | null): number => {
            if (start == null) return 3;
            if (end != null && start <= nowMs && nowMs <= end) return 0; // 진행중
            if (start > nowMs) return 1; // 예정
            return 2; // 종료
        };
        const ga = groupOf(aStart, aEnd);
        const gb = groupOf(bStart, bEnd);
        if (ga !== gb) return ga - gb;
        // 그룹 내 정렬
        if (ga === 0) return (aEnd ?? 0) - (bEnd ?? 0);        // 진행중 — 곧 끝나는 순
        if (ga === 1) return (aStart ?? 0) - (bStart ?? 0);    // 예정 — 임박 순
        return (bEnd ?? 0) - (aEnd ?? 0);                      // 종료 — 최근 종료 순
    };

    // "새로 시작하는 모임" — 아직 끝나지 않은 모임 중 가장 임박한 것들.
    // (이전: 오늘 시작이면 종료 여부 관계없이 노출 → 오전에 끝난 모임이 오후까지 떠있는 회귀가 있어 종료된 건 제외)
    const todayMeetings = meetings.filter(m => {
        if (!m.scheduleStartDateTime) return false;
        if (m.status === '종료') return false;
        // 주최자가 일정 전에 미리 종료 처리한 모임 (status 갱신은 늦더라도) 제외
        if (m.endedAt) return false;
        const startMs = new Date(m.scheduleStartDateTime).getTime();
        const endMs = m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime).getTime() : null;
        const nowMs = now.getTime();

        // 이미 종료 시각이 지난 모임은 제외 (오전 종료 모임 잔존 회귀 수정)
        if (endMs != null && nowMs > endMs) return false;

        // 진행 중 (start ≤ now ≤ end)
        if (startMs <= nowMs && (endMs == null || endMs >= nowMs)) return true;

        const meetingStartDate = new Date(m.scheduleStartDateTime);
        meetingStartDate.setHours(0, 0, 0, 0);
        // 오늘 시작 (아직 시작 전)
        if (meetingStartDate.getTime() === todayStart.getTime()) return true;
        // 여행 모임 진행 중
        if (m.meetingType === 'TRAVEL' && m.scheduleEndDateTime) {
            const meetingEndDate = new Date(m.scheduleEndDateTime);
            meetingEndDate.setHours(0, 0, 0, 0);
            if (meetingStartDate.getTime() < todayStart.getTime() && meetingEndDate.getTime() >= todayStart.getTime()) {
                return true;
            }
        }
        return false;
    }).sort(sortTodayByProximity);

    // 정산 대기 — 종료된 모임 중 정산 완료 처리 안 된 모임 모두. 새로 시작하는 모임 섹션 아래, 예정 모임 위에 노출.
    // (이전: hasUnsettled 만 검사 → 지출 0건 + 종료된 모임이 누락 → 주요기능 탭엔 보이는데 홈엔 안 보이는
    //  불일치. 종료 후 사용자가 홈에서 즉시 지출 등록 진입할 수 있도록 paymentSettledAt 으로 교체.)
    const unsettledFinishedMeetings = meetings.filter(m => {
        if ((m as any).paymentSettledAt) return false;  // 주최자가 정산 완료 처리한 모임은 제외
        const endMs = m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime).getTime() : null;
        // endedAt 도 종료로 간주 (주최자 미리 종료 → status 갱신 전에도 정산 대기 섹션 노출)
        const isEnded = m.status === '종료' || !!m.endedAt || (endMs != null && now.getTime() > endMs);
        return isEnded;
    }).sort((a, b) => {
        // 최근 종료 순
        const ae = a.scheduleEndDateTime ? new Date(a.scheduleEndDateTime).getTime() : 0;
        const be = b.scheduleEndDateTime ? new Date(b.scheduleEndDateTime).getTime() : 0;
        return be - ae;
    });

    const upcomingMeetings = meetings.filter(m => {
        if (m.status === '종료') return false;
        if (m.endedAt) return false;  // 주최자 미리 종료 모임 — 예정에서도 제외
        // 일정 미정 모임도 예정에 포함
        if (!m.scheduleStartDateTime) return true;
        const meetingStartDate = new Date(m.scheduleStartDateTime);
        meetingStartDate.setHours(0, 0, 0, 0);
        // 내일 이후의 모든 예정 모임
        return meetingStartDate >= tomorrowStart;
    }).sort(sortBySchedule);

    console.log('[Home] meetings:', meetings.length, 'today:', todayMeetings.length, 'upcoming:', upcomingMeetings.length);
    console.log('[Home] meetings data:', meetings.map(m => ({ id: m.id, title: m.title, status: m.status, start: m.scheduleStartDateTime, end: m.scheduleEndDateTime })));

    // 홈 진입 시 — 참석+위치공유 ON인 모임들 중 현재 이후인 것들을 네이티브 스케줄로 재등록
    // (앱 재설치 / 다른 기기 / 네이티브 스케줄 증발 대비)
    //
    // 권위 소스 우선순위: 서버의 myLocationSharing > localStorage 캐시.
    // 서버값이 true 면 localStorage 도 미러링해 UI 토글과 동기화.
    const didSyncRef = useRef(false);
    useEffect(() => {
        if (didSyncRef.current) return;
        if (!isNativeApp()) return;
        if (!meetings.length) return;

        const now = Date.now();
        const syncTargets = meetings.filter(m => {
            if (m.status === '종료') return false;
            if (m.myAttendanceStatus !== 'ATTENDING') return false;
            if (!m.scheduleStartDateTime) return false;
            // 서버 우선, 없으면 localStorage 캐시
            const serverSharing = (m as any).myLocationSharing === true;
            const localSharing = localStorage.getItem(`locationSharing_${m.id}`) === 'true';
            if (!serverSharing && !localSharing) return false;
            const endTime = m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime).getTime() : null;
            if (endTime && endTime < now) return false; // 이미 종료
            return true;
        });

        syncTargets.forEach(m => {
            const startTime = new Date(m.scheduleStartDateTime!).getTime();
            const endTime = m.scheduleEndDateTime
                ? new Date(m.scheduleEndDateTime).getTime()
                : startTime + 3 * 60 * 60 * 1000;
            setNativeMeetingSchedule(String(m.id), startTime, endTime, {
                meetingLat: m.latitude,
                meetingLon: m.longitude,
            });
            // localStorage 도 미러 (UI 토글과 동기화)
            localStorage.setItem(`locationSharing_${m.id}`, 'true');
        });

        if (syncTargets.length > 0) {
            console.log('[Home] 네이티브 스케줄 재동기화:', syncTargets.map(m => m.id));
        }
        didSyncRef.current = true;
    }, [meetings]);

    const todayIds = new Set(todayMeetings.map(m => m.id));
    const upcomingIds = new Set(upcomingMeetings.map(m => m.id));

    // 종료된 모임의 정산 유도는 unsettledFinishedMeetings (예정 모임 위 섹션) 으로 통합됨 — 별도 변수 제거

    // 장소 미정 모임
    const undecidedMeetings = meetings.filter(m =>
        m.status !== '종료' && (m.isLocationUndecided || m.isScheduleUndecided) &&
        !todayIds.has(m.id) && !upcomingIds.has(m.id)
    );

    // 참여 중 모임 (나머지)
    const participatingMeetings = meetings.filter(m => {
        if (todayIds.has(m.id) || upcomingIds.has(m.id)) return false;
        if (m.status === '종료') return false;
        if (undecidedMeetings.find(u => u.id === m.id)) return false;
        return true;
    }).sort(sortBySchedule);


    // 참석 미정 모임
    const undecidedAttendance = meetings.filter(m => m.myAttendanceStatus === 'UNDECIDED' && m.status !== '종료');

    const handleAttendanceChange = async (meetingId: string | number, status: 'ATTENDING' | 'NOT_ATTENDING') => {
        try {
            await MeetingRepository.onPutUpdateAttendance(String(meetingId), status);
            meetingStore.onGetMeetingList(); // 목록 새로고침
            const msg = status === 'ATTENDING' ? t('home.toastAttendanceChanged') : t('home.toastAttendanceDeclined');
            setToastMsg(msg);
            setTimeout(() => setToastMsg(''), 2500);
        } catch {
            setToastMsg(t('home.toastAttendanceFailed'));
            setTimeout(() => setToastMsg(''), 2500);
        }
    };

    const themes = getSeasonalThemes();

    const { containerRef: pullRef, pullDistance, isRefreshing: isPullRefreshing } = usePullToRefresh({
        onRefresh: async () => {
            // 전체 데이터 실제 로드 완료까지 대기 → 스피너가 완료 시점에 정확히 꺼짐
            await Promise.all([refreshAll(), fetchLocation(true)]);
        },
    });

    useBackDismiss(!!recreateSourceId, closeRecreate);
    useBackDismiss(showNotification, () => setShowNotification(false));

    // 오늘 모임의 sub_places 자동 fetch — 미니지도 "다음 이동 장소" 표시용
    useEffect(() => {
        todayMeetings.forEach(async (m) => {
            if (todaySubPlaces[String(m.id)]) return;
            try {
                const res = await MeetingRepository.onGetSubPlaces(String(m.id));
                const list = res?.data?.data || res?.data || [];
                setTodaySubPlaces(prev => ({ ...prev, [String(m.id)]: list }));
            } catch {
                setTodaySubPlaces(prev => ({ ...prev, [String(m.id)]: [] }));
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [todayMeetings.map(m => m.id).join(',')]);

    // 좌표 없는 sub_place 백필 — placeAddress / placeName 으로 카카오 geocode 후 todaySubPlaces 업데이트
    useEffect(() => {
        const meetingIds = Object.keys(todaySubPlaces);
        if (meetingIds.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                await loadKakaoSDK();
                if (cancelled || !window.kakao?.maps?.services) return;
                const kakao = window.kakao as any;
                const geocoder = new kakao.maps.services.Geocoder();
                const places = new kakao.maps.services.Places();
                const tryOne = (q: string): Promise<{ lat: number; lng: number } | null> => new Promise((resolve) => {
                    geocoder.addressSearch(q, (r: any[], s: string) => {
                        if (cancelled) return resolve(null);
                        if (s === kakao.maps.services.Status.OK && r.length > 0) {
                            return resolve({ lat: parseFloat(r[0].y), lng: parseFloat(r[0].x) });
                        }
                        places.keywordSearch(q, (r2: any[], s2: string) => {
                            if (cancelled) return resolve(null);
                            if (s2 === kakao.maps.services.Status.OK && r2.length > 0) {
                                return resolve({ lat: parseFloat(r2[0].y), lng: parseFloat(r2[0].x) });
                            }
                            resolve(null);
                        });
                    });
                });

                for (const mid of meetingIds) {
                    const list = todaySubPlaces[mid] || [];
                    const targets = list.filter((sp: any) => (!sp.latitude || !sp.longitude) && (sp.placeAddress || sp.placeName));
                    if (targets.length === 0) continue;
                    const updates = new Map<number, { lat: number; lng: number }>();
                    for (const sp of targets) {
                        const q = (sp.placeAddress || sp.placeName || '').trim();
                        if (!q) continue;
                        const found = await tryOne(q);
                        if (cancelled) return;
                        if (found) updates.set(sp.subPlaceId, found);
                    }
                    if (updates.size > 0 && !cancelled) {
                        setTodaySubPlaces(prev => ({
                            ...prev,
                            [mid]: (prev[mid] || []).map((sp: any) => updates.has(sp.subPlaceId)
                                ? { ...sp, latitude: updates.get(sp.subPlaceId)!.lat, longitude: updates.get(sp.subPlaceId)!.lng }
                                : sp),
                        }));
                    }
                }
            } catch (e) {
                console.warn('[Home] today sub_place geocode 백필 실패:', e);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Object.keys(todaySubPlaces).join(','), Object.values(todaySubPlaces).map((arr: any) => arr.length).join(',')]);

    // 모든 장소 목록 (primary + 좌표 있는 sub_place, HOTEL 제외) — chip 리스트용
    /* 기록은 모임이 시작된 뒤에만 남깁니다 — 시작 전에는 남길 것이 없습니다.
       모임 상세의 canAddPlacePhoto 와 같은 기준입니다. */
    const isJournalOn = useCallback((m: any): boolean => {
        const startMs = m?.scheduleStartDateTime ? new Date(m.scheduleStartDateTime).getTime() : null;
        return startMs != null && Date.now() >= startMs;
    }, []);

    const loadJournalSummary = useCallback(async (m: any) => {
        if (!m?.id || !isJournalOn(m)) return;
        try {
            const res = await PlaceReviewRepository.getMeetingJournalSummary(String(m.id));
            const data = unwrapData<JournalSummary>(res);
            if (data) setJournalSummaries(prev => ({ ...prev, [String(m.id)]: data }));
        } catch {
            /* 요약이 없다고 오늘 카드가 깨지면 안 됩니다 */
        }
    }, [isJournalOn]);

    /* 시작된 오늘 모임만, 한 번씩 */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            for (const m of todayMeetings) {
                if (cancelled) return;
                if (!isJournalOn(m)) continue;
                if (journalSummaries[String(m.id)]) continue;
                await loadJournalSummary(m);
            }
        })();
        return () => { cancelled = true; };
        // journalSummaries 를 의존성에 넣으면 받을 때마다 다시 도는 고리가 됩니다
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [todayMeetings, isJournalOn, loadJournalSummary]);

    /* placeId·subPlaceId·dayNumber 는 기록(우리 기록)이 어디에 붙는지 정하는 데 씁니다.
       지도가 가리키는 곳과 기록이 붙는 곳이 달라지면 안 되므로 같은 목록에서 꺼냅니다. */
    type TodayPlace = {
        key: string; name: string; lat: number; lng: number;
        category?: string; sortTs?: number;
        placeId?: string | null; subPlaceId?: number | null; dayNumber?: number | null;
    };
    const buildAllPlaces = useCallback((m: any): TodayPlace[] => {
        const list: TodayPlace[] = [];
        if (m.latitude && m.longitude) {
            list.push({
                key: 'primary',
                name: m.placeName || m.location || '집결',
                lat: m.latitude, lng: m.longitude,
                placeId: m.placeKey || null,
                category: t('meeting.markerGather'),
                sortTs: m.scheduleStartDateTime ? new Date(m.scheduleStartDateTime).getTime() : undefined,
            });
        }
        const subs = todaySubPlaces[String(m.id)] || [];
        for (const sp of subs) {
            if (sp.category === 'HOTEL') continue;
            const lat = typeof sp.latitude === 'number' ? sp.latitude : (sp.latitude ? parseFloat(sp.latitude) : null);
            const lng = typeof sp.longitude === 'number' ? sp.longitude : (sp.longitude ? parseFloat(sp.longitude) : null);
            if (!lat || !lng) continue;
            let ts: number | undefined;
            if (sp.startDatetime) ts = new Date(sp.startDatetime).getTime();
            else if (sp.visitTime && m.scheduleStartDateTime) {
                const d = new Date(m.scheduleStartDateTime);
                const dayOffset = (sp.dayNumber || 1) - 1;
                if (dayOffset > 0) d.setDate(d.getDate() + dayOffset);
                const mch = String(sp.visitTime).match(/(\d+):(\d+)/);
                if (mch) d.setHours(parseInt(mch[1]), parseInt(mch[2]), 0, 0);
                ts = d.getTime();
            }
            list.push({
                key: `sp-${sp.subPlaceId}`,
                name: sp.placeName,
                lat, lng,
                category: sp.category,
                sortTs: ts,
                placeId: sp.placeKey || sp.placeId || null,
                subPlaceId: sp.subPlaceId ?? null,
                dayNumber: sp.dayNumber ?? null,
            });
        }
        return list;
    }, [todaySubPlaces]);

    // 호텔 좌표 추출 — 여행 모임에서 sub_place 일정이 끝났을 때 다음 destination 으로 사용
    const pickHotelPoint = useCallback((m: any): { lat: number; lng: number; name: string } | null => {
        if (m.meetingType !== 'TRAVEL') return null;
        const subs = todaySubPlaces[String(m.id)] || [];
        const hotels = subs.filter((sp: any) => sp.category === 'HOTEL' && sp.latitude && sp.longitude);
        if (hotels.length > 0) {
            const h = hotels[0];
            return { lat: Number(h.latitude), lng: Number(h.longitude), name: h.placeName || '숙소' };
        }
        // legacy 단일 hotelLatitude/Longitude
        if (m.hotelLatitude && m.hotelLongitude) {
            return { lat: parseFloat(m.hotelLatitude), lng: parseFloat(m.hotelLongitude), name: m.hotelName || '숙소' };
        }
        return null;
    }, [todaySubPlaces]);

    // 다음 이동 장소 좌표 결정 — primary 가 시작했으면 미래 sub_place 우선
    const pickNextMapPoint = useCallback((m: any): { lat: number; lng: number; name: string } | null => {
        const subs = todaySubPlaces[String(m.id)] || [];
        const nowMs = Date.now();
        const startMs = m.scheduleStartDateTime ? new Date(m.scheduleStartDateTime).getTime() : null;
        const isTravel = m.meetingType === 'TRAVEL';

        const primaryPoint = (m.latitude && m.longitude)
            ? { lat: m.latitude, lng: m.longitude, name: m.placeName || m.location || '' }
            : null;

        // primary 가 미래(아직 시작 안 함) → primary 좌표
        if (startMs && startMs > nowMs) {
            return primaryPoint;
        }

        const candidates = subs
            .filter((sp: any) => sp.category !== 'HOTEL' && sp.latitude && sp.longitude)
            .map((sp: any) => {
                let ts: number | null = null;
                if (sp.startDatetime) {
                    ts = new Date(sp.startDatetime).getTime();
                } else if (sp.visitTime && m.scheduleStartDateTime) {
                    const d = new Date(m.scheduleStartDateTime);
                    const dayOffset = (sp.dayNumber || 1) - 1;
                    if (dayOffset > 0) d.setDate(d.getDate() + dayOffset);
                    const mch = String(sp.visitTime).match(/(\d+):(\d+)/);
                    if (mch) d.setHours(parseInt(mch[1]), parseInt(mch[2]), 0, 0);
                    ts = d.getTime();
                }
                return { sp, ts };
            });

        // 미래 sub_place 가장 가까운 — 현재 시각 이후 첫 sub_place 를 "다음 목적지" 로 선택.
        const future = candidates.filter(c => c.ts != null && c.ts >= nowMs);
        if (future.length > 0) {
            const target = [...future].sort((a, b) => (a.ts as number) - (b.ts as number))[0].sp;
            return { lat: Number(target.latitude), lng: Number(target.longitude), name: target.placeName };
        }
        // 모든 sub_place 지남 — 여행이면 hotel (숙소로 복귀).
        const hotelPoint = pickHotelPoint(m);
        if (hotelPoint) return hotelPoint;
        // 미래 없으면 가장 최근 sub_place
        if (candidates.length > 0) {
            const target = [...candidates].sort((a, b) => (b.ts || 0) - (a.ts || 0))[0].sp;
            return { lat: Number(target.latitude), lng: Number(target.longitude), name: target.placeName };
        }
        // sub_place 없으면 primary
        if (m.latitude && m.longitude) return { lat: m.latitude, lng: m.longitude, name: m.placeName || m.location || '' };
        return null;
    }, [todaySubPlaces]);

    /* ══════════════════════════════════════════════════════════════
     * 화면
     *
     * 형태는 components/ds 가 맡고, 여기서는 무엇을 어디에 담을지만 정합니다.
     *
     *   HeroBanner   오늘 예정하는 모임 — 큰 사진 배너 (모임 상세 상단도 같은 형태를 씁니다)
     *   PhotoStrip   번개 만들기 — 사진 위에 글을 얹은 낮은 배너
     *   MeetingCard  모임 목록 — 작은 썸네일 + 제목 + 일시/장소 + 배지
     *   PlaceCard    둘러보는 것 — 가로 레일
     *   NoticeRow    답을 기다리는 알림 — 초대 · 안전귀가 · 활동
     * ════════════════════════════════════════════════════════════ */

    /** 장소 사진 — externalPlaceId 가 있는 모임만 */
    const [placePhotoMap, setPlacePhotoMap] = useState<Record<string, PlacePhoto>>({});
    const photoFetchedRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const ids = Array.from(new Set(
            meetings
                .map(m => (m as any).externalPlaceId as string | null)
                .filter((id): id is string => !!id && !photoFetchedRef.current.has(id as string))
        ));
        if (ids.length === 0) return;
        ids.forEach(id => photoFetchedRef.current.add(id));
        (async () => {
            try {
                const photos = await fetchPlacePhotos(ids);
                const next: Record<string, PlacePhoto> = {};
                Object.entries(photos).forEach(([id, v]) => { if (v?.photoUrl) next[id] = v; });
                if (Object.keys(next).length > 0) setPlacePhotoMap(prev => ({ ...prev, ...next }));
            } catch { /* 사진은 없어도 카드가 성립합니다 */ }
        })();
    }, [meetings]);

    const photoOf = (m: any): string | null => {
        const id = m?.externalPlaceId;
        return id ? (placePhotoMap[id]?.photoUrl || null) : null;
    };

    /** 배너 우하단 썸네일용 — 대표 사진을 앞에 두고 중복은 걸러 냅니다 */
    const photoListOf = (m: any): string[] => {
        const id = m?.externalPlaceId;
        const pd = id ? placePhotoMap[id] : undefined;
        if (!pd) return [];
        return Array.from(new Set([pd.photoUrl, ...(pd.photos || [])].filter(Boolean) as string[]));
    };

    const goDetail = (id: any) => navigate(`/meeting/detail/${id}`);

    /** 모임 배지 */
    const meetingBadges = (m: any): { label: string; tone: BadgeTone }[] => {
        const list: { label: string; tone: BadgeTone }[] = [];
        if (m.meetingType === 'TRAVEL') list.push({ label: t('home.travelTag'), tone: 'lime' });
        if (m.isRegularMeeting) list.push({ label: t('home.regularTag'), tone: 'neutral' });
        if (m.creatorNo === currentUserNo) list.push({ label: t('home.hostTag'), tone: 'weak' });
        if (m.raceEnabled) list.push({ label: m.raceFinalizedAt ? t('home.raceDone') : t('home.raceTag'), tone: 'weak' });
        return list;
    };

    const whenOf = (m: any) => m.isScheduleUndecided
        ? t('home.scheduleNotSet')
        : [m.date, m.time].filter(Boolean).join(' · ');

    const whereOf = (m: any) => m.isLocationUndecided
        ? t('meeting.placeNotSet')
        : (m.location || t('meeting.placeNotSet'));

    /** "5명 중 3명 참여" — 목록 API 가 멤버 목록을 주지 않아 숫자로 씁니다 */
    const memberLine = (m: any) => Number(m.attendingCount) > 0
        ? t('home.attendingOfTotal', { total: m.memberCount, going: m.attendingCount })
        : t('home.members', { count: m.memberCount });

    /** 알림 줄 — 초대 · 안전귀가 · 활동이 같은 형태를 씁니다 */
    const NoticeRow = ({
        icon, title, sub, ctaLabel, onCta, onDismiss, onClick, accent,
    }: {
        icon: React.ReactNode; title: string; sub?: string;
        ctaLabel?: string; onCta?: () => void; onDismiss?: () => void;
        onClick?: () => void; accent?: boolean;
    }) => (
        <div className={accent ? `${hs.notice} ${hs.noticeAccent}` : hs.notice} onClick={onClick}>
            <span className={hs.noticeIcon}>{icon}</span>
            <span className={hs.noticeBody}>
                <span className={hs.noticeTitle}>{title}</span>
                {sub ? <span className={hs.noticeSub}>{sub}</span> : null}
            </span>
            {ctaLabel && onCta ? (
                <button type="button" className={hs.noticeCta} onClick={(e) => { e.stopPropagation(); onCta(); }}>
                    {ctaLabel}
                </button>
            ) : null}
            {onDismiss ? (
                <button
                    type="button"
                    className={hs.noticeClose}
                    onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                    aria-label={t('home.closeAria')}
                >
                    <IcClose size={16} />
                </button>
            ) : null}
        </div>
    );

    /** 모임 카드 한 장 — 목록 섹션이 전부 이 형태를 씁니다 */
    const meetingCardOf = (m: any, opts?: {
        when?: string; where?: string; time?: React.ReactNode;
        extraBadges?: { label: string; tone: BadgeTone }[];
        onClick?: () => void;
    }) => (
        <MeetingCard
            key={m.id}
            name={m.title}
            when={opts?.when ?? whenOf(m)}
            where={opts?.where ?? whereOf(m)}
            time={opts?.time ?? (m.dDay ? <TimeBadge size="sm">{m.dDay}</TimeBadge> : undefined)}
            imageUrl={photoOf(m)}
            placeName={m.placeName || undefined}
            badges={[...meetingBadges(m), ...(opts?.extraBadges || [])]}
            people={<span className={hs.cardMembers}>{memberLine(m)}</span>}
            onClick={opts?.onClick ?? (() => goDetail(m.id))}
        />
    );

    const dismissActivity = (id: number) => {
        setDismissedActivityIds(prev => {
            const next = new Set(prev);
            next.add(id);
            try { localStorage.setItem('home_dismissed_activity', JSON.stringify(Array.from(next))); } catch { /* ignore */ }
            return next;
        });
    };

    /** 초대 "참여하기" — 가입 + ATTENDING 사전 설정 후 상세로 */
    const joinFromInvite = async (meetingId: any) => {
        try {
            await MeetingRepository.onPostJoinMeeting(meetingId);
            try {
                await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: 'ATTENDING' });
            } catch { /* 실패해도 상세에서 재시도 */ }
        } catch (err) {
            // 이미 가입된 경우가 있어 에러는 무시 — 최종 판단은 상세에서
            console.warn('[invite] join failed', err);
        }
        goDetail(meetingId);
    };

    /* ── 화면이 쓰는 파생 목록 ── */

    const visibleInvites = pendingInviteDetails.filter(
        inv => !dismissedActivityIds.has(-inv.inviteId) && withinDay(inv.invitedAt)
    );
    const visibleActivity = recentActivity.filter(a => {
        if (a.pushValue === 'MEETING_MEMBER_JOINED') {
            if (initialDismissedRef.current.has(a.pushId)) return false;
        } else if (dismissedActivityIds.has(a.pushId)) {
            return false;
        }
        return a.pushValue !== 'MEETING_INVITED';
    });
    const regularMeetings = meetings.filter(m => m.isRegularMeeting && m.status !== '종료');
    const votingMeetings = meetings.filter(m => m.hasActiveProposal);
    const freshTreats = todayTreats.filter((tr: any) => withinDay(tr.createdAt || tr.created_at || tr.createdDt));
    const recentRace = meetings.filter(m => m.raceEnabled && m.raceFinalizedAt &&
        (Date.now() - new Date(m.raceFinalizedAt as any).getTime()) < 2 * 60 * 60 * 1000).slice(0, 1);

    const activityIcon: Record<string, React.FC<{ size?: number }>> = {
        MEETING_INVITED: IcEnvelope,
        SCHEDULE_CONFIRMED: IcCalendar,
        SCHEDULE_PROPOSED: IcBallot,
        MEETING_MEMBER_JOINED: IcParty,
    };

    /* 가이드에서 "미니게임 보러가기" 를 누르면 이 자리로 데려갑니다 */
    const gamesRef = useRef<HTMLDivElement>(null);
    const goGames = () => gamesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    /* 인사말은 여러 개 중 하나를 무작위로 뽑습니다. 매 렌더마다 다시 뽑으면
       화면이 자리 잡는 동안 문구가 빠르게 바뀌다가 마지막 것으로 멈춥니다 —
       "엄청 빠른 속도로 이것저것 바뀐다" 는 제보가 이것이었습니다.
       아래 번개 문구와 같은 기준으로, 이름과 시(hour) 가 그대로면 고정합니다.
       시간대가 바뀌면(아침→오후) 그때 새로 뽑습니다. */
    const greetingName = userName || t('home.friend');
    const greeting = useMemo(
        () => getGreeting(greetingName),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [greetingName, new Date().getHours()],
    );
    /* 번개 문구와 사진은 같은 시간대 판정에서 함께 나옵니다.
       매 렌더마다 다시 뽑으면 문구가 깜빡이므로 시(hour) 단위로 고정합니다. */
    const flashCta = useMemo(
        () => getFlashCTACopy({ hasActiveFlash: activeFlashes.length > 0 }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeFlashes.length, new Date().getHours()],
    );

    /* 축하(내가 쏜다 · 레이스 1등)와 번개 만들기는 성격이 다릅니다.
       축하는 "알려주는 것"이라 알림 줄에, 번개는 "지금 누르라"는 것이라
       참고 앱의 사진 배너(TopicBlock)에 담습니다. */
    const treat = freshTreats[0];
    const raceWin = recentRace[0]
        ? { m: recentRace[0], winner: raceResultsMap[String(recentRace[0].id)]?.find(r => r.rank === 1) }
        : null;
    const celebration = treat
        ? {
            title: t('home.treatTitle', { who: treat.paidByNickname || t('home.fallbackPaidByNickname') }),
            sub: [meetings.find(m => String(m.id) === String(treat.meetingId))?.title, treat.storeName]
                .filter(Boolean).join(' · '),
            onClick: () => goDetail(treat.meetingId),
        }
        : raceWin?.winner
            ? {
                title: t('home.raceWinnerTitle', { who: raceWin.winner.nickname }),
                sub: raceWin.m.title + ' · ' + t('home.raceFirstPlace'),
                onClick: () => goDetail(raceWin.m.id),
            }
            : null;



    /* ── 오늘 예정하는 모임 — 화면에서 큰 사진을 쓰는 유일한 자리 ──
     *
     * 컴포넌트가 아니라 "그리는 함수" 입니다. 부르는 쪽에서 renderTodayHero(m)
     * 으로 씁니다.
     *
     * 예전에는 <TodayHero /> 였습니다. 컴포넌트를 Home 안에서 정의하면 렌더마다
     * 새로운 타입이 되고, React 는 그걸 다른 컴포넌트로 보고 이전 것을 통째로
     * 언마운트합니다 — 기록을 쓰던 시트가 그때 같이 사라졌습니다. 홈에서 무슨
     * 일이든 일어나면(시계 틱·데이터 도착·키보드 리사이즈) 쓰던 글과 고르던
     * 사진이 날아갔습니다.
     *
     * 훅이 하나도 없으므로 그냥 부르면 됩니다. 반환한 JSX 가 Home 의 트리에
     * 그대로 들어가, 안쪽 컴포넌트들이 자리를 지켜 상태를 잃지 않습니다.
     * (훅을 넣게 되면 이 방법은 깨집니다 — 그때는 밖으로 빼야 합니다.)
     */
    const renderTodayHero = (m: any) => {
        const allPlaces = buildAllPlaces(m);
        /* 우리 기록 — 값은 Home 이 받아 둡니다.
           TodayHero 는 Home 안에서 정의된 컴포넌트라 렌더마다 새로 만들어지고,
           그때마다 통째로 다시 마운트됩니다. 여기서 fetch 하면 렌더마다 다시
           부르게 되므로 훅을 두면 안 됩니다. */
        const journalOn = isJournalOn(m);
        const journalSummary = journalSummaries[String(m.id)] || null;
        const nextPoint = pickNextMapPoint(m) || pickHotelPoint(m);
        const selectedKey = todaySelectedKeys[String(m.id)]
            || (nextPoint
                ? (allPlaces.find(p => Math.abs(p.lat - nextPoint.lat) < 1e-4 && Math.abs(p.lng - nextPoint.lng) < 1e-4)?.key || allPlaces[0]?.key)
                : allPlaces[0]?.key)
            || null;
        const activePlace = allPlaces.find(p => p.key === selectedKey) || allPlaces[0] || null;
        const mapLat = activePlace?.lat ?? (m.latitude || 0);
        const mapLng = activePlace?.lng ?? (m.longitude || 0);
        const mapName = activePlace?.name || m.placeName || m.location || t('meeting.placeNotSet');

        const openMap = () => {
            // 네이티브 위임은 해외 전용. 카카오 타일이 해외 좌표에서 흰 화면이 되기 때문입니다.
            // 한국 장소까지 위임하면 카카오맵이 보여야 할 자리에 네이티브 구글맵이 뜹니다.
            if (isNativeApp() && mapLat && mapLng && !isInKorea(mapLat, mapLng)) {
                openNativeRealtimeLocation({
                    meetingId: String(m.id),
                    destination: { name: mapName, address: '', lat: mapLat, lng: mapLng },
                });
                return;
            }
            navigate(`/meeting/location/${m.id}`, {
                state: mapLat && mapLng ? { destinationOverride: { lat: mapLat, lng: mapLng, name: mapName } } : undefined,
            });
        };

        const until = m.scheduleStartDateTime
            ? formatTimeUntil(
                new Date(m.scheduleStartDateTime).getTime(),
                m.scheduleEndDateTime ? new Date(m.scheduleEndDateTime).getTime() : null,
                Date.now(),
                t,
            )
            : null;

        const tags: FeedTagItem[] = [];
        if (until) tags.push({ label: until.text });
        if (m.dDay) tags.push({ label: m.dDay });
        meetingBadges(m).forEach(b => tags.push({ label: b.label }));

        return (
            <>
            <HeroBanner
                imageUrl={photoOf(m)}
                photos={photoListOf(m)}
                tags={tags}
                title={m.title}
                paragraph={`${mapName} · ${memberLine(m)}`}
                date={whenOf(m)}
                onClick={() => goDetail(m.id)}
                actions={
                    <>
                        {/* 공유 여부는 색이 아니라 아이콘 모양(핀 / 사선 그은 핀)으로도 읽히게 합니다 */}
                        <Button
                            variant="brand"
                            size="md"
                            icon={locationSharingMap[String(m.id)] ? <IconPin size={16} /> : <IconPinOff size={16} />}
                            onClick={() => toggleLocationSharing(m.id)}
                        >
                            {locationSharingMap[String(m.id)]
                                ? t('home.locationSharingOn')
                                : t('home.locationSharingOff')}
                        </Button>
                        <Button variant="secondary" size="md" icon={<IconMap size={16} />} onClick={openMap}>
                            {t('home.viewLocation')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="md"
                            icon={<IconShare size={16} />}
                            onClick={() => openShare({
                                title: m.title,
                                text: m.title,
                                url: getMeetingShareUrl(m.inviteCode || String(m.id)),
                            })}
                        >
                            {t('common.share')}
                        </Button>
                    </>
                }
            >
                {/* 장소 사진이 없으면 지도가 배너를 채웁니다 */}
                {mapLat && mapLng ? (
                    <MapContainer
                        center={{ lat: mapLat, lng: mapLng }}
                        markers={[
                            { id: `today-${m.id}`, lat: mapLat, lng: mapLng, label: 'D', tooltip: mapName, isDestination: true },
                            ...(userGpsLocation ? [{
                                id: `today-me-${m.id}`, lat: userGpsLocation.lat, lng: userGpsLocation.lng,
                                label: t('meeting.markerMe'), tooltip: t('meeting.markerMyLoc'),
                            }] : []),
                        ]}
                        height={456}
                        zoomLevel={isInKorea(mapLat, mapLng) ? 7 : 13}
                        fitBounds={Boolean(userGpsLocation)}
                    />
                ) : null}
            </HeroBanner>

            {/* 우리 기록 — 오늘 카드는 지금까지 장소·지도·멤버까지만 보여 줬습니다.
               멤버들이 남긴 사진과 글이 여기 붙습니다. 앵커는 지도가 가리키는
               그 장소를 그대로 씁니다. */}
            {journalOn && activePlace?.placeId && (
                <div className={hs.heroJournal}>
                    <JournalSummaryCard
                        meetingId={String(m.id)}
                        summary={journalSummary}
                        anchor={{
                            placeId: activePlace.placeId,
                            placeName: activePlace.name,
                            dayNumber: activePlace.dayNumber ?? undefined,
                            subId: activePlace.subPlaceId ?? undefined,
                            label: activePlace.name,
                        }}
                        nickname={nickname}
                        profileUrl={profileImgPath}
                        onPosted={() => loadJournalSummary(m)}
                    />
                </div>
            )}
        </>
        );
    };

    return (
        <div className={hs.wrap}>
            {/* ── 상단바 ── */}
            <div className={hs.bar}>
                <AppBar
                    mode="plain"
                    title="DOKOCREW"
                    right={
                        <span className={hs.barActions}>
                            <button
                                type="button"
                                className={hs.barBtn}
                                onClick={() => requestApp(WEB_TO_APP.GET_LOCATION_DEBUG, { ts: Date.now() })}
                                aria-label="location debug"
                            >
                                <IcBug size={20} />
                            </button>
                            <button
                                type="button"
                                className={hs.barBtn}
                                onClick={() => setShowNotification(true)}
                                aria-label={t('home.notification')}
                            >
                                <IcBell size={20} />
                            </button>
                        </span>
                    }
                />
            </div>

            {/* [임시:NATIVE-MAP-PREVIEW] 네이티브 지도 확인 링크 —
                앱에는 URL 입력창이 없어 /ds/native-maps 로 갈 방법이 없습니다.
                확인이 끝나면 이 블록만 지우면 됩니다. CSS 모듈을 건드리지 않으려고
                인라인 스타일을 썼습니다. */}
            <button
                type="button"
                onClick={() => navigate('/ds/native-maps')}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '10px var(--t-gutter)',
                    background: 'var(--t-ink-1)', border: 0,
                    fontFamily: 'var(--t-font-family)', fontSize: 12, fontWeight: 700,
                    color: 'var(--t-surface)', cursor: 'pointer',
                }}
            >
                <span>네이티브 지도 확인 (임시)</span>
                <span aria-hidden>›</span>
            </button>

            <div className={hs.scroll} ref={pullRef}>
                {/* 당겨서 새로고침 */}
                <div className={hs.ptr} style={{ height: pullDistance > 0 ? pullDistance : 0 }}>
                    <span
                        className={isPullRefreshing ? `${hs.ptrSpinner} ${hs.ptrSpinning}` : hs.ptrSpinner}
                        style={!isPullRefreshing ? { transform: `rotate(${pullDistance * 4}deg)` } : undefined}
                    />
                </div>

                {/* ── 인사 — 검정 띠가 아니라 평문 제목 ── */}
                <div className={hs.greeting}>
                    {/* 가장 가까운 약속이 있으면 인사 대신 그걸 알려 줍니다 */}
                    <span className={hs.greetingSub}>
                        {upcomingSoonMeeting
                            ? `${t('home.nearestIs')} ${upcomingSoonMeeting.dDay || upcomingSoonMeeting.date || ''}`
                                + (upcomingSoonMeeting.placeName ? ` · ${upcomingSoonMeeting.placeName}` : '')
                            : greeting.sub}
                    </span>
                    <h1 className={hs.greetingTitle}>{greeting.headline}</h1>
                    {statusMessage && (
                        <button type="button" className={hs.statusMsg} onClick={() => navigate('/settings/profile')}>
                            <IcChat size={16} /> {statusMessage}
                        </button>
                    )}
                </div>

                {/* ── 오늘 예정하는 모임 ── */}
                {!isLoading && todayMeetings.length > 0 && (
                    <Section spacing="tight">
                        <Bleed>
                            <div className={hs.heroStack}>
                                {todayMeetings.map((m) => (
                                    <Fragment key={m.id}>{renderTodayHero(m)}</Fragment>
                                ))}
                            </div>
                        </Bleed>
                    </Section>
                )}

                {/* ══ 답을 기다리는 알림 ══ */}
                {!isLoading && (celebration || visibleInvites.length > 0 || safeReturnInvites.length > 0 || visibleActivity.length > 0) && (
                    <Section spacing="tight">
                        <div className={hs.noticeList}>
                            {celebration && (
                                <NoticeRow
                                    icon={<IcParty size={20} />}
                                    title={celebration.title}
                                    sub={celebration.sub}
                                    onClick={celebration.onClick}
                                />
                            )}
                            {visibleInvites.map(inv => (
                                <NoticeRow
                                    key={`inv-${inv.inviteId}`}
                                    icon={<IcEnvelope size={20} />}
                                    title={t('home.inviteArrived')}
                                    sub={t('home.inviteBy', { who: inv.inviterNickname || t('home.someone'), meeting: inv.meetingName })}
                                    ctaLabel={t('home.inviteParticipate')}
                                    onCta={() => joinFromInvite(inv.meetingId)}
                                    onDismiss={() => dismissActivity(-inv.inviteId)}
                                    onClick={() => joinFromInvite(inv.meetingId)}
                                    accent
                                />
                            ))}
                            {safeReturnInvites.map(inv => (
                                <NoticeRow
                                    key={`sr-${inv.sessionId}`}
                                    icon={<IcHome size={20} />}
                                    title={t('home.safeReturnRequest')}
                                    sub={t('home.safeReturnBy', { who: inv.travelerNickname })}
                                    ctaLabel={t('common.confirm')}
                                    onCta={() => navigate(`/safe-return/watch/${inv.sessionId}`)}
                                    onClick={() => navigate(`/safe-return/watch/${inv.sessionId}`)}
                                    onDismiss={() => setSafeReturnInvites(prev => prev.filter(s => s.sessionId !== inv.sessionId))}
                                    accent
                                />
                            ))}
                            {visibleActivity.map(a => {
                                const Icon = activityIcon[a.pushValue] || IcParty;
                                const path = a.linkUrl?.match(/\/meeting\/([^/?#]+)/);
                                return (
                                    <NoticeRow
                                        key={a.pushId}
                                        icon={<Icon size={20} />}
                                        title={a.title}
                                        sub={a.content}
                                        onClick={() => {
                                            if (path) goDetail(path[1]);
                                            else if (a.linkUrl) navigate(a.linkUrl);
                                        }}
                                        onDismiss={() => dismissActivity(a.pushId)}
                                    />
                                );
                            })}
                        </div>
                    </Section>
                )}

                {/* 진행 중인 번개 — 후보 득표 */}
                {!isLoading && activeFlashes.length > 0 && (
                    <Section spacing="tight">
                        {activeFlashes.map((f: any) => {
                            const cands = [...(f.candidates || [])].sort((a: any, b: any) => b.voteCount - a.voteCount);
                            const remainMin = Math.max(0, Math.round((new Date(f.voteDeadline).getTime() - Date.now()) / 60000));
                            const max = Math.max(1, ...cands.map((x: any) => x.voteCount));
                            const dec = (s: string) => { try { return decodeURIComponent(s || ''); } catch { return s; } };
                            return (
                                <div key={f.flashId} className={hs.flash}>
                                    <div className={hs.flashHead}>
                                        <span className={hs.flashLabel}>{t('tone.homeFlash.activeTitle')}</span>
                                        <TimeBadge size="sm" urgent={remainMin > 0 && remainMin <= 15}>
                                            {remainMin > 0
                                                ? t('tone.homeFlash.activeDeadline', { minutes: remainMin })
                                                : t('tone.homeFlash.activeDeadlineEnded')}
                                        </TimeBadge>
                                    </div>
                                    <div className={hs.flashName}>{dec(f.title)}</div>
                                    <div className={hs.flashBars}>
                                        {cands.slice(0, 3).map((c: any) => (
                                            <div key={c.candidateId} className={hs.flashBarRow}>
                                                <span className={hs.flashBarName}>{dec(c.placeName)}</span>
                                                <span className={hs.flashBar}>
                                                    <span className={hs.flashBarFill} style={{ width: `${(c.voteCount / max) * 100}%` }} />
                                                </span>
                                                <span className={hs.flashBarCount}>{t('tone.homeFlash.voteCount', { count: c.voteCount })}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <Button variant="secondary" size="sm" onClick={() => navigate(`/flash/status/${f.flashId}`)}>
                                        {t('tone.homeFlash.viewStatus')}
                                    </Button>
                                </div>
                            );
                        })}
                    </Section>
                )}

                {/* ══ 번개 만들기 — 참고 앱의 사진 배너 ══ */}
                {!isLoading && meetings.length > 0 && (
                    <Section spacing="tight">
                        <Bleed>
                            <PhotoStrip
                                imageUrl={flashCta.image}
                                tag={t('home.flashTag')}
                                title={flashCta.title}
                                sub={flashCta.sub}
                                onClick={() => navigate('/meeting/create/place-explore?mode=flash')}
                            />
                        </Bleed>
                    </Section>
                )}

                {/* 오늘도 예정도 없으면 번개 배너만 남아 화면이 비어 보입니다.
                    그럴 때는 주요기능 탭과 같은 "모임 만들기" 를 바로 아래 둡니다. */}
                {!isLoading && todayMeetings.length === 0 && upcomingMeetings.length === 0 && (
                    <CreateMeetingSection spacing="tight" />
                )}

                {/* 로딩 */}
                {isLoading && (
                    <Section spacing="tight">
                        <div className={hs.skelTitle} />
                        <CardGrid>
                            {[1, 2, 3].map(i => (
                                <div key={i} className={hs.skelCard}>
                                    <div className={hs.skelThumb} />
                                    <div className={hs.skelLines}>
                                        <div className={hs.skelLine} style={{ width: '70%' }} />
                                        <div className={hs.skelLine} style={{ width: '45%' }} />
                                    </div>
                                </div>
                            ))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 참석 알려주세요 ══ */}
                {!isLoading && undecidedAttendance.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.tellAttendance')}
                            count={undecidedAttendance.length}
                            sub={t('home.tellAttendanceSub')}
                        />
                        <CardGrid>
                            {undecidedAttendance.map(m => (
                                <div key={m.id} className={hs.askCard}>
                                    {meetingCardOf(m)}
                                    <div className={hs.askBtns}>
                                        <Button variant="primary" size="sm" onClick={() => handleAttendanceChange(m.id, 'ATTENDING')}>
                                            {t('home.attend')}
                                        </Button>
                                        <Button variant="secondary" size="sm" onClick={() => handleAttendanceChange(m.id, 'NOT_ATTENDING')}>
                                            {t('home.decline')}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 참여 중 ══ */}
                {!isLoading && participatingMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionParticipating')}
                            count={participatingMeetings.length}
                            moreLabel={t('common.viewAll')}
                            onMore={() => navigate('/meetings')}
                        />
                        <CardGrid>
                            {participatingMeetings.slice(0, 5).map(m => meetingCardOf(m))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 정산 대기 중 ══ */}
                {!isLoading && unsettledFinishedMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionUnsettledFinished')}
                            count={unsettledFinishedMeetings.length}
                            sub={t('home.unsettledSub')}
                        />
                        <CardGrid>
                            {unsettledFinishedMeetings.map(m => {
                                const balance = Number((m as any).myBalanceAmount || 0);
                                const unsettled = Number((m as any).myUnsettledAmount || 0);
                                const money = balance > 0
                                    ? { label: `${t('home.receiveLabel')} ${t('home.wonAmount', { amount: balance.toLocaleString() })}`, tone: 'brand' as BadgeTone }
                                    : unsettled > 0
                                        ? { label: `${t('home.sendLabel')} ${t('home.wonAmount', { amount: unsettled.toLocaleString() })}`, tone: 'danger' as BadgeTone }
                                        : { label: t('home.enterSettlement'), tone: 'outline' as BadgeTone };
                                return meetingCardOf(m, {
                                    time: <TimeBadge size="sm" past>{t('home.pastMeeting')}</TimeBadge>,
                                    extraBadges: [money],
                                    onClick: () => navigate(`/meeting/detail/${m.id}`, { state: { scrollToSettlement: true } }),
                                });
                            })}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 다가올 모임 ══ */}
                {!isLoading && upcomingMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionUpcoming')}
                            count={upcomingMeetings.length}
                            moreLabel={t('common.viewAll')}
                            onMore={() => navigate('/meetings?filter=upcoming')}
                        />
                        <CardGrid>
                            {upcomingMeetings.map(m => meetingCardOf(m, {
                                extraBadges: m.hasUnsettled
                                    ? [{ label: t('home.hasUnsettled'), tone: 'danger' as BadgeTone }]
                                    : [],
                            }))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 일정 투표 진행 중 ══ */}
                {!isLoading && votingMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionVote')}
                            count={votingMeetings.length}
                            sub={t('home.voteSub')}
                        />
                        <CardGrid>
                            {votingMeetings.map(m => meetingCardOf(m, {
                                when: t('home.votedCount', { count: m.proposalVoteCount || 0 }),
                                extraBadges: [{ label: t('home.goVote'), tone: 'brand' as BadgeTone }],
                                onClick: () => navigate(`/schedule/main/${m.id}`),
                            }))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 준비 필요해요 ══ */}
                {!isLoading && undecidedMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionNeedsPrep')}
                            count={undecidedMeetings.length}
                            sub={t('home.needsPrepSub')}
                        />
                        <CardGrid>
                            {undecidedMeetings.map(m => (
                                <div key={m.id} className={hs.askCard}>
                                    {meetingCardOf(m, {
                                        extraBadges: [
                                            ...(m.isScheduleUndecided ? [{ label: t('home.scheduleNotSet'), tone: 'outline' as BadgeTone }] : []),
                                            ...(m.isLocationUndecided ? [{ label: t('meeting.placeNotSet'), tone: 'outline' as BadgeTone }] : []),
                                        ],
                                    })}
                                    <div className={hs.askBtns}>
                                        {m.isScheduleUndecided && (
                                            <Button variant="secondary" size="sm" onClick={() => navigate(`/schedule/main/${m.id}`)}>
                                                {t('home.scheduleAdjust')}
                                            </Button>
                                        )}
                                        {m.isLocationUndecided && (
                                            <Button variant="secondary" size="sm" onClick={() => navigate(`/meeting/edit/${m.id}`)}>
                                                {t('home.decidePlaceBtn')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </CardGrid>
                    </Section>
                )}

                {/* ══ 정기 모임 ══ */}
                {!isLoading && regularMeetings.length > 0 && (
                    <Section divided>
                        <SectionHeader
                            title={t('home.sectionRegular')}
                            count={regularMeetings.length}
                            sub={t('home.regularSub')}
                        />
                        <CardGrid>
                            {regularMeetings.map(m => meetingCardOf(m))}
                        </CardGrid>
                    </Section>
                )}

                {/* 아직 모임이 없는 사람 */}
                {!isLoading && meetings.length === 0 && (
                    <Section divided>
                        <SectionHeader title={t('homeComp.emptyTitle')} sub={t('homeComp.emptySub')} />
                        <Button variant="primary" size="lg" block onClick={() => navigate('/meeting/create')}>
                            {t('meeting.create')}
                        </Button>
                    </Section>
                )}

                {/* 빠른 시작 · 오늘의 팁 — 숨기면 섹션째 사라지도록
                   껍데기를 각 컴포넌트가 직접 그립니다 */}
                <QuickStart />
                <DailyTip onGoGames={goGames} />

                {/* ══ 미니게임 — 모임 없이 바로 ══ */}
                <Section divided>
                    <SectionHeader title={t('home.minigame')} sub={t('home.minigameSub')} />
                    <HomeGames ref={gamesRef} />
                </Section>

                {/* ══ 초대 코드 ══ */}
                <Section spacing="tight">
                    <Bleed>
                        <button type="button" className={hs.invite} onClick={() => navigate('/join-code')}>
                            {t('home.inviteCode')}
                            <span className={hs.inviteCta}>{t('homeComp.enterInviteCode')}</span>
                        </button>
                    </Bleed>
                </Section>

                {/* ══ 지금 뜨는 모임 ══ */}
                {themes.length > 0 && (
                    <Section divided>
                        <SectionHeader title={t('home.sectionTrending')} sub={t('home.trendingSub')} />
                        <CardRail>
                            {themes.map((theme, idx) => (
                                <PlaceCard
                                    key={idx}
                                    name={theme.title}
                                    meta={theme.subtitle}
                                    imageUrl={theme.imageUrl}
                                    onClick={() => navigate('/meeting/create/theme-recommend', {
                                        state: {
                                            themeName: theme.title, themeType: theme.type, themeEmoji: theme.emoji,
                                            themeSubtitle: theme.subtitle, themeKeyword: theme.keyword,
                                            meetingType: theme.type === 'travel' ? 'TRAVEL' : 'DAY',
                                        },
                                    })}
                                />
                            ))}
                        </CardRail>
                    </Section>
                )}

                {/* ══ 이런 모임 어때요? ══ */}
                <Section divided last>
                    <SectionHeader title={t('home.sectionSuggestion')} sub={t('home.suggestionSub')} />
                    <CardRail>
                        {FIXED_THEMES.map((theme, idx) => {
                            const tt = theme.i18nKey ? t(`home.themes.${theme.i18nKey}.title`) : theme.title;
                            const ts = theme.i18nKey ? t(`home.themes.${theme.i18nKey}.subtitle`) : theme.subtitle;
                            return (
                                <PlaceCard
                                    key={idx}
                                    name={tt}
                                    meta={ts}
                                    imageUrl={theme.imageUrl}
                                    wide
                                    onClick={() => navigate('/meeting/create/theme-recommend', {
                                        state: {
                                            themeName: tt, themeType: theme.type, themeEmoji: theme.emoji,
                                            themeSubtitle: ts, themeKeyword: theme.keyword,
                                            meetingType: theme.type === 'travel' ? 'TRAVEL' : 'DAY',
                                        },
                                    })}
                                />
                            );
                        })}
                    </CardRail>
                </Section>

                <div className={hs.tail} />
            </div>

            {/* ── 겹쳐 뜨는 것들 ── */}

            {/* 지난 모임 다시 만들기 */}
            {recreateSourceId && recreateSource && (
                <div className={hs.sheetOverlay} onClick={closeRecreate}>
                    <div className={hs.sheet} onClick={(e) => e.stopPropagation()}>
                        <div className={hs.sheetHandle} />
                        <h2 className={hs.sheetTitle}>{t('home.recreateSame')}</h2>
                        <p className={hs.sheetSub}>{recreateSource.meetingName}</p>

                        <label className={hs.fieldLabel} htmlFor="recreate-date">{t('home.newDate')}</label>
                        <input
                            id="recreate-date"
                            type="date"
                            className={hs.field}
                            value={recreateDate}
                            min={toLocalISOString(new Date()).slice(0, 10)}
                            onChange={(e) => setRecreateDate(e.target.value)}
                        />

                        <span className={hs.fieldLabel}>{t('home.timeLabel')}</span>
                        <div className={hs.fieldRow}>
                            <input
                                type="time"
                                className={hs.field}
                                value={recreateStartTime}
                                onChange={(e) => setRecreateStartTime(e.target.value)}
                                aria-label={t('home.timeLabel')}
                            />
                            <span className={hs.fieldSep}>~</span>
                            <input
                                type="time"
                                className={hs.field}
                                value={recreateEndTime}
                                onChange={(e) => setRecreateEndTime(e.target.value)}
                                aria-label={t('home.timeLabel')}
                            />
                        </div>

                        <span className={hs.fieldLabel}>{t('home.placeLabel')}</span>
                        <div className={hs.placeRow}>
                            <span className={hs.placeValue}>
                                {recreatePlace?.name || recreatePlace?.address || t('meeting.placeNotSet')}
                                {recreatePlace?.address && recreatePlace.name && (
                                    <span className={hs.placeAddr}>{recreatePlace.address}</span>
                                )}
                            </span>
                            <Button variant="secondary" size="sm" onClick={goChangePlace}>
                                {t('home.change')}
                            </Button>
                        </div>

                        <p className={hs.sheetNote}>{t('home.recreateNote')}</p>
                        <div className={hs.sheetActions}>
                            <Button variant="secondary" size="lg" block onClick={closeRecreate}>
                                {t('home.recreateCancel')}
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                block
                                onClick={confirmRecreate}
                                disabled={!recreateDate || !recreateStartTime || !recreateEndTime || isRecreating}
                            >
                                {isRecreating ? t('home.creating') : t('home.create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* 외부 지도앱 */}
            {showExtMapId && (() => {
                const m = meetings.find(mt => String(mt.id) === showExtMapId);
                return m ? (
                    <ExternalMapSheet
                        visible
                        placeName={m.location || ''}
                        address={''}
                        lat={m.latitude || 0}
                        lng={m.longitude || 0}
                        onClose={() => setShowExtMapId(null)}
                    />
                ) : null;
            })()}

            {toastMsg && <div className={hs.toast}>{toastMsg}</div>}

            {/* 모임 만들기 */}
            <button type="button" className={hs.fab} onClick={() => navigate('/meeting/create')}>
                <IcPlus size={18} />
                <span>{t('meeting.create')}</span>
            </button>

            {/* 시작 가이드 — 처음 온 사람에게 한 번. "다시 보지 않기" 로 영구히 접힙니다 */}
            <StartGuide
                meetings={meetings}
                hasMyPayment={(meetings || []).some(m => Number((m as any).myPaidAmount || 0) > 0)}
                userNo={currentUserNo}
                onGoGames={goGames}
            />

            <StartPointer hasMeetings={(meetings?.length || 0) > 0} />

            <AppGnb />

            {/* 알림 */}
            <Sheet
                open={showNotification}
                onClose={() => setShowNotification(false)}
                title={t('home.notification')}
                closeLabel={t('common.close')}
            >
                <CommonEmbed
                    title={t('home.notification')}
                    path="/notification"
                    showHeader={false}
                    service={config.SERVICE_ID}
                    onClose={() => setShowNotification(false)}
                />
            </Sheet>

            {/* 정산 등록/수정 */}
            {costModal && (
                <CostInput
                    inModal
                    meetingIdProp={costModal.meetingId}
                    onClose={() => setCostModal(null)}
                    onSaved={() => meetingStore.onGetMeetingList()}
                />
            )}

            {/* 메인 팝업 (사용팁/공지) */}
            {mainPopup && (
                <AppPopupModal
                    popup={mainPopup}
                    userId={sessionStorage.getItem('userId') || ''}
                    onClose={() => setMainPopup(null)}
                />
            )}
        </div>
    );
};
